
export interface AuditErrorDefinition {
    code: string;
    title: string;
    description: string;
    fixPrompt: string;
    severity: 'error' | 'suggestion' | 'warning';
}

// Helper to enforce type on values while inferring keys
function defineErrors<T extends string>(errors: Record<T, AuditErrorDefinition>) {
    return errors;
}

export const AUDIT_ERRORS = defineErrors({
    // FORMATTING (FRM)
    'FRM-01': {
        code: 'FRM-01',
        title: 'Cabeçalho de Cena Incorreto',
        description: 'O cabeçalho deve obrigatoriamente incluir o tempo de luz (DIA, NOITE, etc). Termos como "CONTÍNUO" não substituem a iluminação principal.',
        fixPrompt: 'Ajuste para: INT./EXT. LOCAL (EM CAIXA ALTA) - [DIA/NOITE] - CONTÍNUO. Todo o cabeçalho deve estar obrigatoriamente em MAIÚSCULAS.',
        severity: 'error'
    },
    'FRM-02': {
        code: 'FRM-02',
        title: 'Espaçamento de Personagem (Glue Rule)',
        description: 'Existe um espaço ilegal entre o nome do personagem e o diálogo.',
        fixPrompt: 'Remova a linha em branco entre o nome do personagem e o início da fala.',
        severity: 'error'
    },
    'FRM-03': {
        code: 'FRM-03',
        title: 'Rótulo de Título Desnecessário',
        description: 'O título do roteiro contém o rótulo "TITLE:" ou "TÍTULO:".',
        fixPrompt: 'Remova o prefixo "TITLE:" ou "TÍTULO:". O título deve ser apenas o texto em CAIXA ALTA.',
        severity: 'error'
    },
    'FRM-04': {
        code: 'FRM-04',
        title: 'Hierarquia de Local Invertida',
        description: 'O cabeçalho descreve o sub-local antes do local principal.',
        fixPrompt: 'Inverta a ordem para [LOCAL PRINCIPAL] – [SUB-LOCAL] – [PERÍODO]. Use sempre CAIXA ALTA. Ex: FORMIGUEIRO – TÚNEL – CONTÍNUO.',
        severity: 'error'
    },
    'FRM-05': {
        code: 'FRM-05',
        title: 'Formatação de Flashback',
        description: 'Embora o uso de FLASHBACK no início do cabeçalho seja aceitável, o padrão mais comum e profissional na indústria é colocá-lo ao final da linha.',
        fixPrompt: 'Mova o rótulo para o final do cabeçalho. Ex: INT. LOCAL - DIA - FLASHBACK.',
        severity: 'suggestion'
    },
    'FRM-06': {
        code: 'FRM-06',
        title: 'Nomes de Personagens Duplicados',
        description: 'O nome do personagem aparece repetido duas vezes seguidas sem diálogo entre eles.',
        fixPrompt: 'Remova o nome duplicado e garanta que exista apenas uma linha de personagem antes do diálogo.',
        severity: 'error'
    },
    'FRM-07': {
        code: 'FRM-07',
        title: 'Marcadores de Capítulo/Numeração',
        description: 'O roteiro contém divisões de capítulo ou numeração de transição/cena, o que não é recomendado em Spec Scripts.',
        fixPrompt: 'Remova marcadores de capítulo e numerações de cena/transição.',
        severity: 'suggestion'
    },
    'FRM-08': {
        code: 'FRM-08',
        title: 'Uso Incorreto de FIM/THE END',
        description: 'O termo "FIM" ou "THE END" centralizado foi usado fora do encerramento absoluto do roteiro.',
        fixPrompt: 'Mantenha "FIM" apenas no final absoluto. Para transições, use termos padrão como "CORTA PARA:".',
        severity: 'error'
    },
    'FRM-09': {
        code: 'FRM-09',
        title: 'Contato Mal Formatado',
        description: 'E-mail e telefone de contato devem estar em linhas separadas no canto inferior da capa.',
        fixPrompt: 'Separe o e-mail e o telefone em duas linhas distintas.',
        severity: 'suggestion'
    },
    'FRM-10': {
        code: 'FRM-10',
        title: 'Título Fora de Caixa Alta',
        description: 'O título do roteiro na capa deve estar sempre em MAIÚSCULAS.',
        fixPrompt: 'Converta o título do roteiro para CAIXA ALTA.',
        severity: 'error'
    },
    'FRM-11': {
        code: 'FRM-11',
        title: 'Erro na Linha de Crédito',
        description: 'O rótulo "Escrito por" e o nome do autor devem estar na mesma linha.',
        fixPrompt: 'Una "Escrito por" e o nome do autor na mesma linha.',
        severity: 'error'
    },
    'FRM-12': {
        code: 'FRM-12',
        title: 'Erro no Rótulo "Baseado em,"',
        description: 'O rótulo "Baseado em," exige o uso da vírgula e deve estar centralizado.',
        fixPrompt: 'Ajuste para "Baseado em," com a vírgula obrigatória.',
        severity: 'error'
    },
    'FRM-13': {
        code: 'FRM-13',
        title: 'Cabeçalho Fora de Caixa Alta',
        description: 'Todos os elementos do cabeçalho (Local, Sub-local, Tempo e indicadores como FLASHBACK) devem estar em letras MAIÚSCULAS.',
        fixPrompt: 'Converta todo o cabeçalho (incluindo sub-locais e tempos) para CAIXA ALTA. Ex: INT. FORMIGUEIRO - TÚNEL PRINCIPAL - DIA - FLASHBACK.',
        severity: 'error'
    },
    'FRM-14': {
        code: 'FRM-14',
        title: 'Extensão de Personagem Isolada',
        description: 'Extensões como (V.O.), (O.S.) ou (CONT\'D) devem obrigatoriamente estar na mesma linha do nome do personagem.',
        fixPrompt: 'Una a extensão ao nome do personagem na mesma linha. Ex: "JOÃO (V.O.)"',
        severity: 'error'
    },
    'FRM-15': {
        code: 'FRM-15',
        title: 'Inconsistência de Tempo/Luz',
        description: 'O tempo indicado no cabeçalho (ex: DIA) contradiz a descrição da cena (ex: "o sol se põe").',
        fixPrompt: 'Ajuste o tempo no cabeçalho para condizer com o visual (ex: mude de DIA para ENTARDECER ou NOITE).',
        severity: 'error'
    },
    'ACT-01': {
        code: 'ACT-01',
        title: 'Bloco de Ação Muito Longo',
        description: 'Parágrafos de descrição devem ter no máximo 4 a 5 linhas.',
        fixPrompt: 'Divida o bloco de ação em parágrafos menores de no máximo 4 linhas.',
        severity: 'suggestion'
    },
    'ACT-02': {
        code: 'ACT-02',
        title: 'Direção de Câmera em Spec Script',
        description: 'Evite termos técnicos de câmera como CLOSE ON, ANGLE ON ou CÂMERA ACOMPANHA na descrição. Transições padrão como "CORTA PARA:" são permitidas.',
        fixPrompt: 'REESCREVA A AÇÃO SEM TERMOS TÉCNICOS. Descreva apenas o visual. Não explique a remoção.',
        severity: 'error'
    },
    'ACT-03': {
        code: 'ACT-03',
        title: 'Estado Interno/Psicologia (Não Filmável)',
        description: 'A descrição deve mostrar apenas o que a câmera registra. Evite descrever o que o personagem "sente", "pensa", "sabe" ou "percebe".',
        fixPrompt: 'TRANSFORME EM COMPORTAMENTO: NUNCA apenas remova a emoção. EXTERNALIZE o sentimento abstrato transformando-o em uma ação ou reação física. Ex: Em vez de "em choque", use "Suas antenas tremem e ela dá um passo para trás". Em vez de "Ele está exausto", use "Ele desaba no chão, ofegante".',
        severity: 'error'
    },
    'ACT-04': {
        code: 'ACT-04',
        title: 'Psicologia/Emoção na Ação (Show, Don\'t Tell)',
        description: 'Uso de termos puramente abstratos ou internos (ex: "Ela sente medo", "Determinação em seus olhos"). Manifestações físicas visíveis (ex: "Chora", "Grita", "Ri") são permitidas e recomendadas.',
        fixPrompt: 'TRANSFORME EM COMPORTAMENTO: Substitua o sentimento abstrato por uma ação física única e definitiva. Se está nervoso, use "Suas mãos tremem". Se está triste, use "Uma lágrima escorre". Evite o vaivém de edições sutis; escolha a ação mais forte e pare nela.',
        severity: 'error'
    },
    'ACT-05': {
        code: 'ACT-05',
        title: 'Exposição/Explicação em Ação',
        description: 'O bloco de ação explica a biologia, história ou sentimentos internos que a câmera não vê. NOTA: Sub-cabeçalhos informativos (ex: "TREINOS") ou adjetivos visuais não são erros aqui.',
        fixPrompt: 'Reescreva descrições abstratas para serem visuais. Mantenha a ação, mas remova explicações invisíveis.',
        severity: 'suggestion'
    },
    'ACT-10': {
        code: 'ACT-10',
        title: 'Lixo de IA / Metadados',
        description: 'O roteiro contém notas da IA, saudações ou explicações técnicas fora do formato.',
        fixPrompt: 'Remova apenas notas da IA ou saudações. MANTENHA TODO O CONTEÚDO NARRATIVO DO ROTEIRO.',
        severity: 'error'
    },
    'ACT-11': {
        code: 'ACT-11',
        title: 'Formatação de Legenda não padrão',
        description: 'Uso de termos como "LEGEND:" ou formatos inventados para textos na tela.',
        fixPrompt: 'Substitua "LEGEND:" ou similares pelo padrão profissional "TITLE ON SCREEN:" ou "SUPER:".',
        severity: 'error'
    },
    'ACT-12': {
        code: 'ACT-12',
        title: 'Linguagem Literária/Não-Visual (Sight & Sound Only)',
        description: 'Uso de metáforas poéticas ou sentidos infilmáveis NA DESCRIÇÃO DE CENA (Ação). (Permitido em Diálogos/V.O.).',
        fixPrompt: 'REESCREVA A DESCRIÇÃO DE FORMA CONCRETA E VISUAL. Elimine metáforas. Apenas o que se vê e ouve.',
        severity: 'error'
    },
    'ACT-13': {
        code: 'ACT-13',
        title: 'Narração de Livro (Novelização)',
        description: 'O texto narra a história como um livro (ex: "Eles se tornaram amigos", "O tempo passou"). O roteiro deve descrever apenas o AGORA.',
        fixPrompt: 'Transforme o resumo narrativo em ações presentes. Em vez de "Marcos é uma figura paternal", mostre Marcos cuidando de alguém no presente.',
        severity: 'error'
    },
    'ACT-14': {
        code: 'ACT-14',
        title: 'Capitalização de Som/FX',
        description: 'Sons importantes e efeitos visuais cruciais devem ser capitalizados (CAIXA ALTA) em sua primeira aparição na ação. ATENÇÃO: Personagens (seres vivos/entidades) NÃO são SFX.',
        fixPrompt: 'Coloque em CAIXA ALTA apenas o nome do som ou efeito visual importante mencionado (ex: UM TIRO ECOA). Nomes de personagens já introduzidos permanecem em minúsculas.',
        severity: 'suggestion'
    },
    'ACT-15': {
        code: 'ACT-15',
        title: 'Excesso de Adjetivos e Julgamentos',
        description: 'Uso de adjetivos subjetivos ou avaliações inferidas. NOTA: Se o adjetivo for puramente visual (cor, intensidade), a auditoria gera uma sugestão de revisão, não erro de formatação',
        fixPrompt: 'OBJETIVIDADE MÁXIMA: Substitua adjetivos por medidas ou fatos físicos sempre que possível. Forneça a correção final de uma vez.',
        severity: 'suggestion'
    },
    'ACT-16': {
        code: 'ACT-16',
        title: 'Capitalização Excessiva na Ação',
        description: 'Objetos comuns e locais genéricos não devem estar em CAIXA ALTA na ação para dar ênfase literária.',
        fixPrompt: 'Converta para minúsculas (ex: "a fazenda", "o sol"). Mantenha CAIXA ALTA apenas para Personagens (1ª vez), Sons importantes e Efeitos visuais.',
        severity: 'suggestion'
    },
    'DLG-01': {
        code: 'DLG-01',
        title: 'Diálogo Expositivo (On-the-nose)',
        description: 'O personagem diz exatamente o que sente ou explica a trama de forma artificial.',
        fixPrompt: 'Refine o diálogo para usar subtexto. Mostre o sentimento através da ação ou entonação, não da explicação direta.',
        severity: 'suggestion'
    },
    'DLG-02': {
        code: 'DLG-02',
        title: 'V.O. / Narração Formatada como Ação',
        description: 'Indicações de narração (ex: "A narração explica") ou falas de Voice Over não devem estar em blocos de descrição.',
        fixPrompt: 'Formate adequadamente como Diálogo: NOME (V.O.) seguido do texto da fala. Remova referências narrativas como "A narração diz".',
        severity: 'error'
    },
    'DLG-03': {
        code: 'DLG-03',
        title: 'Ação Dentro do Diálogo (Lei da Quebra)',
        description: 'É ESTRITAMENTE PROIBIDO descrever ações físicas ou narração de cena dentro de um bloco de diálogo. Isso inclui casos onde o bloco inteiro é uma descrição narrativa em terceira pessoa (ação) posicionada como fala. ATENÇÃO: Se o personagem apenas MENCIONA uma ação ou objeto (ex: "Vou sair agora"), isso é DIÁLOGO legítimo e NÃO deve ser marcado como erro.',
        fixPrompt: 'APLIQUE A LEI DA QUEBRA: Extraia a ação para um novo parágrafo de Ação. Se o bloco descreve um som (ex: rosnar, rir, gritar), você DEVE "curar" a fala inserindo uma onomatopeia adequada (ex: "Grrrrrrr", "Hahaha", "Aaaaah!") coerente com a rubrica, mantendo o bloco de fala ativo, e ENTÃO mova a ação para baixo.',
        severity: 'error'
    },
    'DLG-04': {
        code: 'DLG-04',
        title: 'Rubrica Interpretativa/Emocional',
        description: 'Rubricas devem indicar apenas INTENÇÃO ou TOM DE VOZ (ex: sussurra, sarcástico). Máximo 3 palavras. PROIBIDO descrever ações físicas. PROIBIDO quebra de linha ou hifenização. OBRIGATÓRIO: Texto da rubrica em letras minúsculas (ex: "sussurra"). PROIBIDO ESPAÇOS: Jamais use espaços antes ou dentro (ex: " (texto)" ou "( texto )"). A RUBRICA DEVE ESTAR SOZINHA EM SUA PRÓPRIA LINHA.',
        fixPrompt: 'Remova a ação física da rubrica. Se for uma ação visível ou tiver mais de 3 palavras, mova para uma linha de Ação. Converta o texto para minúsculas. Mantenha nos parênteses apenas o tom de voz. REMOVA ESPAÇOS: Garanta que o parêntese/colchete esteja colado ao texto e sem espaços no início da linha.',
        severity: 'error'
    },
    'DLG-06': {
        code: 'DLG-06',
        title: 'Diálogo na Mesma Linha da Rubrica',
        description: 'O diálogo jamais pode começar na mesma linha da rubrica (parênteses). A rubrica deve estar sozinha em sua linha própria.',
        fixPrompt: 'Dê um "Enter" depois do parêntese final para jogar a fala do personagem para a linha de baixo.',
        severity: 'error'
    },
    'DLG-07': {
        code: 'DLG-07',
        title: 'Hifenização no Corpo do Diálogo',
        description: 'Palavras não devem ser hifenizadas ou quebradas entre linhas no bloco de diálogo (fala). A palavra deve ser mantida inteira na linha atual ou movida integralmente para a próxima linha.',
        fixPrompt: 'Remova hifens de quebra de palavra e garanta que o termo esteja inteiro em uma única linha.',
        severity: 'error'
    },
    'DLG-05': {
        code: 'DLG-05',
        title: 'Capitalização Irregular no Diálogo',
        description: 'Uso de letras maiúsculas no meio de frases sem justificativa (nome próprio ou ênfase técnica).',
        fixPrompt: 'Corrija a capitalização para seguir as regras gramaticais padrão (apenas nomes próprios e início de frase).',
        severity: 'suggestion'
    },
    'DLG-08': {
        code: 'DLG-08',
        title: 'Eco de Transcrição (Diálogo Duplicado)',
        description: 'O texto exato do diálogo foi repetido logo abaixo como uma linha de ação, ou o diálogo inteiro está indevidamente em CAIXA ALTA.',
        fixPrompt: 'Remova a linha de ação duplicada e converta o texto do diálogo para formato de frase normal (apenas a primeira letra maiúscula).',
        severity: 'error'
    },
    'CHR-01': {
        code: 'CHR-01',
        title: 'Introdução de Personagem Fora do Padrão',
        description: 'Uso incorreto de conceitos narrativos em vez de descrição visual na introdução.',
        fixPrompt: 'Ajuste para: NOME (TIPO/IDADE), descrição visual. Ex: "ÁUREA (FORMIGA OPERÁRIA), carapaça rachada". Evite conceitos invisíveis.',
        severity: 'error'
    },
    'CHR-02': {
        code: 'CHR-02',
        title: 'Nome de Personagem em Minúscula',
        description: 'O nome do personagem acima do diálogo deve estar obrigatoriamente em CAIXA ALTA.',
        fixPrompt: 'Coloque o nome do personagem em CAIXA ALTA.',
        severity: 'error'
    },
    'CHR-03': {
        code: 'CHR-03',
        title: 'Primeira Aparição de Personagem',
        description: 'A primeira vez que um personagem é introduzido na Ação, seu nome deve estar em CAIXA ALTA. ATENÇÃO: Personagens NÃO são SFX.',
        fixPrompt: 'Capitalize o nome do personagem nesta primeira aparição na ação.',
        severity: 'error'
    },
    'CHR-04': {
        code: 'CHR-04',
        title: 'Personagem Fantasma (Voz sem Corpo)',
        description: 'Um personagem tem fala (DIÁLOGO), mas não foi introduzido ou mencionado na ação imediatamente anterior. Ele surge do nada.',
        fixPrompt: 'Insira uma linha de ação antes da fala descrevendo o personagem ou sua entrada em cena (ex: "UM MERGULHADOR ajusta os óculos.").',
        severity: 'error'
    },
    'FRM-16': {
        code: 'FRM-16',
        title: 'Uso Incorreto de CONTÍNUO',
        description: 'O marcador CONTÍNUO só deve ser usado se a cena ocorre imediatamente após a anterior. Não pode ser usado na primeira cena do roteiro.',
        fixPrompt: 'Remova "CONTÍNUO" ou substitua por um tempo real (DIA/NOITE) se esta for a primeira cena ou se houver salto temporal.',
        severity: 'suggestion'
    },
    'FRM-17': {
        code: 'FRM-17',
        title: 'Acabamento/Encerramento Incorreto',
        description: 'O roteiro acaba abruptamente ou usa transições internas (como CORTA PARA:) para finalizar. O padrão da indústria exige um fechamento formal.',
        fixPrompt: 'Para fechar a "tampa" do projeto: pule duas linhas após a fala final e insira um FADE OUT. (alinhado à direita) ou um FIM. (centralizado). Remova qualquer "CORTA PARA:" no final.',
        severity: 'error'
    },
    'FRM-18': {
        code: 'FRM-18',
        title: 'Cabeçalho Órfão (Final de Página)',
        description: 'Um cabeçalho de cena aparece sozinho no final de uma página. Cabeçalhos devem ter pelo menos uma linha de ação ou diálogo abaixo deles na mesma página.',
        fixPrompt: 'Adicione uma quebra de página (Page Break) antes do cabeçalho de cena para movê-lo para o topo da próxima página.',
        severity: 'error'
    },
    'FRM-19': {
        code: 'FRM-19',
        title: 'Resíduo de Markdown e Artefatos de TI',
        description: 'O texto contém resíduos de formatação Markdown (asteriscos *, underscores _, cerquilhas #) ou artefatos de código de TI (como os três acentos graves ```) que são ilegais em um roteiro profissional. Isso inclui símbolos de ênfase em palavras ou marcas após o FADE OUT.',
        fixPrompt: 'FORNEÇA O TEXTO LIMPO SEM MARKDOWN. Remova ```, *, _, #. Se houver texto após FIM/FADE OUT, apague-o. Apenas o script.',
        severity: 'error'
    },
    'FRM-20': {
        code: 'FRM-20',
        title: 'Falso Diálogo (Ação Formatada como Fala)',
        description: 'Detectado um bloco de texto centralizado (formato de Diálogo) cujo conteúdo é puramente descritivo, narrativo ou visual (Ação). Frequentemente ocorre quando a IA confunde a introdução de um personagem com o início de uma fala.',
        fixPrompt: 'Converta IMEDIATAMENTE este bloco para o formato de AÇÃO (Alinhado à Esquerda). Se houver um nome de personagem acima (CUE), delete-o e integre o nome à primeira frase da ação (Ex: De MARCOS [quebra] Ele corre. para Marcos corre.).',
        severity: 'error'
    },
    'ACT-17': {
        code: 'ACT-17',
        title: 'Sensações Não Visuais na Ação',
        description: 'O roteiro deve descrever apenas o que pode ser visto ou ouvido. Sensações internas não são filmáveis.',
        fixPrompt: 'Substitua sensações como "Cheiro forte" ou "Lembranças voltam" por evidências visuais ou sonoras. Som é permitido (ex: UM TROVÃO RESSOA).',
        severity: 'error'
    },
    'ACT-18': {
        code: 'ACT-18',
        title: 'Linguagem Literária/Poética',
        description: 'O roteiro não deve soar como romance ou poema. Evite metáforas dramáticas sobre o futuro ou destino.',
        fixPrompt: 'Substitua frases literárias como "A escuridão engole o futuro" por descrições concretas e físicas do que está acontecendo AGORA.',
        severity: 'error'
    },
    'ACT-19': {
        code: 'ACT-19',
        title: 'Construção Literária (Prosa)',
        description: 'Termos como "De repente" são construções literárias de prosa. O roteiro deve descrever a ação de forma direta e imediata.',
        fixPrompt: 'Remova "De repente" e descreva a ação diretamente. Ex: "Ela tropeça" em vez de "De repente, ela tropeça".',
        severity: 'suggestion'
    },
    'ACT-20': {
        code: 'ACT-20',
        title: 'Descrição Técnica de Imagem',
        description: 'O roteiro Spec (Master Scene) não descreve cor de imagem, textura de película ou tratamentos visuais de memória. Esses elementos pertencem à pós-produção.',
        fixPrompt: 'Remova descrições de cor, textura ou tratamiento de imagen. Foque exclusivamente na ação física observável.',
        severity: 'suggestion'
    },
    'ACT-21': {
        code: 'ACT-21',
        title: 'Direção de Câmera Narrativa (Visto de...)',
        description: 'Evite descrever ângulos de câmera como "Visto de cima", "Aérea" ou "Ponto de vista". Foque na ação, não na lente.',
        fixPrompt: 'Reescreva focado na ação. De: "Visto de cima: Nadadores..." Para: "Nadadores formam uma linha na água...".',
        severity: 'suggestion'
    },
    'ACT-22': {
        code: 'ACT-22',
        title: 'Meta-Descrição ou Resumo de Cena',
        description: 'O texto descreve que "vemos uma cena" ou "uma sequência de..." em vez de descrever a ação direta. O roteiro deve mostrar o acontecimento, não resumir sua existência.',
        fixPrompt: 'TRANSFORME EM AÇÃO DIRETA: Em vez de "Vemos cenas de crianças brincando", descreva "Crianças pulam corda e correm". Remova "Cenas de...", "Sequência de..." ou "Vemos".',
        severity: 'error'
    },
    'LOG-01': {
        code: 'LOG-01',
        title: 'Erro de Permanência de Objeto',
        description: 'Um objeto aparece ou desaparece sem explicação lógica ou menção prévia (ex: saca uma arma sem tê-la pegado antes).',
        fixPrompt: 'Adicione uma ação prévia onde o personagem pega/porta o objeto ou remova o uso do objeto se ele não deveria estar lá.',
        severity: 'error'
    },
    'LOG-02': {
        code: 'LOG-02',
        title: 'Ação Fisicamente Impossível',
        description: 'O personagem realiza uma ação que contradiz a física do cenário ou sua posição anterior (ex: sai por uma janela em um porão sem janelas).',
        fixPrompt: 'Ajuste a ação para ser fisicamente possível dentro do contexto espacial estabelecido.',
        severity: 'error'
    },
    'STR-01': {
        code: 'STR-01',
        title: 'Ausência de Mudança de Valor',
        description: 'A cena termina no mesmo estado emocional ou situacional em que começou. Tecnicamente, uma cena deve girar o valor (ex: de Positivo para Negativo).',
        fixPrompt: 'Introduza um conflito ou revelação que mude a carga da cena. Se começou em paz, termine em conflito. Se começou em dúvida, termine em decisão.',
        severity: 'warning'
    },
    'STR-02': {
        code: 'STR-02',
        title: 'Falta de Objetivo do Personagem',
        description: 'O protagonista da cena não parece ter um objetivo claro ou uma necessidade dramática imediata.',
        fixPrompt: 'Estabeleça o que o personagem quer alcançar nesta cena específica. O desejo conduz a ação.',
        severity: 'warning'
    },
    'STR-03': {
        code: 'STR-03',
        title: 'Ausência de Conflito/Antagonismo',
        description: 'A cena progride sem resistência. Personagens concordam muito facilmente ou não há forças opostas.',
        fixPrompt: 'Introduza obstáculos, sejam internos (dúvida), interpessoais (outro personagem) ou ambientais.',
        severity: 'warning'
    },
    'STR-04': {
        code: 'STR-04',
        title: 'Ritmo Estático',
        description: 'A cena é composta apenas por diálogo ou apenas por ação repetitiva, sem progressão narrativa.',
        fixPrompt: 'Varie o ritmo. Intercale diálogos significativos com batidas de ação que movam a história adiante.',
        severity: 'warning'
    },
    'LOG-03': {
        code: 'LOG-03',
        title: 'Cenário Impossível (INT./EXT.)',
        description: 'O cabeçalho define um ambiente "INT." (Interior) para um local naturalmente aberto (ex: MAR, CÉU, FLORESTA, DESERTO) sem estar contido (ex: SUBMARINO, ESTAÇÃO). Mar é sempre EXT., mesmo debaixo d\'água.',
        fixPrompt: 'Mude para EXT. se for ao ar livre. Para cenas subaquáticas, use "EXT. MAR - DEBAIXO D\'ÁGUA". Use INT. apenas se estiver DENTRO de uma estrutura construída.',
        severity: 'error'
    },
    'HDR-01': {
        code: 'HDR-01',
        title: 'Inconsistência de Nome de Cenário',
        description: 'O mesmo local deve ser nomeado de forma idêntica em todos os cabeçalhos para evitar erros na decupagem de produção.',
        fixPrompt: 'Padronize o nome deste cenário utilizando o termo já estabelecido anteriormente no roteiro.',
        severity: 'warning'
    },
    'ACT-23': {
        code: 'ACT-23',
        title: 'Destaque Técnico (SFX/Props)',
        description: 'Efeitos sonoros (SFX) e objetos de cena (Props) importantes devem estar em CAIXA ALTA na sua primeira aparição. ATENÇÃO: Personagens NÃO são Props.',
        fixPrompt: 'Coloque o termo técnico ou o objeto fundamental em CAIXA ALTA. Não capitalize nomes de personagens além da sua primeira introdução.',
        severity: 'suggestion'
    },
    'STY-01': {
        code: 'STY-01',
        title: 'Limpeza de Spec Script',
        description: 'Remova números de cena laterais e transições redundantes (ex: CORTA PARA:) que não possuem função dramática específica.',
        fixPrompt: 'Delete os números de cena e limpe as transições desnecessárias para o padrão Master Scene.',
        severity: 'error'
    },
});

export type AuditErrorKey = keyof typeof AUDIT_ERRORS;

export const AUDIT_ERRORS_EN: Record<AuditErrorKey, AuditErrorDefinition> = {
    'FRM-01': {
        code: 'FRM-01',
        title: 'Incorrect Scene Heading',
        description: 'The heading must include the time of day (DAY, NIGHT, etc). Terms like "CONTINUOUS" do not replace the main lighting.',
        fixPrompt: 'Adjust to: INT./EXT. LOCATION (ALL CAPS) - [DAY/NIGHT] - CONTINUOUS. The entire heading must be in ALL CAPS.',
        severity: 'error'
    },
    'FRM-02': {
        code: 'FRM-02',
        title: 'Character Spacing (Glue Rule)',
        description: 'There is an illegal space between the character name and the dialogue.',
        fixPrompt: 'Remove the blank line between the character name and the beginning of the speech.',
        severity: 'error'
    },
    'FRM-03': {
        code: 'FRM-03',
        title: 'Unnecessary Title Label',
        description: 'The script title contains the label "TITLE:" or "TÍTULO:".',
        fixPrompt: 'Remove the prefix "TITLE:" or "TÍTULO:". The title should just be the text in ALL CAPS.',
        severity: 'error'
    },
    'FRM-04': {
        code: 'FRM-04',
        title: 'Inverted Location Hierarchy',
        description: 'The heading describes the sub-location before the main location.',
        fixPrompt: 'Invert the order to [MAIN LOCATION] – [SUB-LOCATION] – [PERIOD]. Use ALL CAPS.',
        severity: 'error'
    },
    'FRM-05': {
        code: 'FRM-05',
        title: 'Flashback Formatting',
        description: 'While the use of FLASHBACK at the beginning of the heading is acceptable, the industry standard is to place it at the end of the line.',
        fixPrompt: 'Move the label to the end of the heading. Ex: INT. LOCATION - DAY - FLASHBACK.',
        severity: 'suggestion'
    },
    'FRM-06': {
        code: 'FRM-06',
        title: 'Duplicate Character Names',
        description: 'The character name appears twice in a row without dialogue between them.',
        fixPrompt: 'Remove the duplicate name and ensure there is only one character line before the dialogue.',
        severity: 'error'
    },
    'FRM-07': {
        code: 'FRM-07',
        title: 'Chapter Markers/Numbering',
        description: 'The script contains chapter divisions or scene/transition numbering, which is not recommended in Spec Scripts.',
        fixPrompt: 'Remove chapter markers and scene/transition numbers.',
        severity: 'suggestion'
    },
    'FRM-08': {
        code: 'FRM-08',
        title: 'Incorrect usage of FIN/THE END',
        description: 'The centered term "THE END" was used outside the absolute end of the script.',
        fixPrompt: 'Keep "THE END" only at the very end. For transitions, use standard terms like "CUT TO:".',
        severity: 'error'
    },
    'FRM-09': {
        code: 'FRM-09',
        title: 'Malformed Contact Format',
        description: 'Contact email and phone number should be on separate lines at the bottom left of the title page.',
        fixPrompt: 'Separate email and phone number into two distinct lines.',
        severity: 'suggestion'
    },
    'FRM-10': {
        code: 'FRM-10',
        title: 'Title Not in All Caps',
        description: 'The script title on the title page must always be in UPPERCASE.',
        fixPrompt: 'Convert the script title to ALL CAPS.',
        severity: 'error'
    },
    'FRM-11': {
        code: 'FRM-11',
        title: 'Credit Line Error',
        description: 'The "Written by" label and the author\'s name should be on the same line.',
        fixPrompt: 'Join "Written by" and the author\'s name on the same line.',
        severity: 'error'
    },
    'FRM-12': {
        code: 'FRM-12',
        title: '"Based on" Label Error',
        description: 'The "Based on," label requires a comma and must be centered.',
        fixPrompt: 'Adjust to "Based on," with the required comma.',
        severity: 'error'
    },
    'FRM-13': {
        code: 'FRM-13',
        title: 'Heading Not in All Caps',
        description: 'All heading elements (Location, Sub-location, Time and indicators) must be in ALL CAPS.',
        fixPrompt: 'Convert the entire heading to ALL CAPS.',
        severity: 'error'
    },
    'FRM-14': {
        code: 'FRM-14',
        title: 'Isolated Character Extension',
        description: 'Extensions like (V.O.), (O.S.), or (CONT\'D) must be on the same line as the character name.',
        fixPrompt: 'Join the extension to the character name on the same line. Ex: "JOHN (V.O.)"',
        severity: 'error'
    },
    'FRM-15': {
        code: 'FRM-15',
        title: 'Time/Light Inconsistency',
        description: 'The time indicated in the heading (e.g. DAY) contradicts the scene description (e.g. "the sun sets").',
        fixPrompt: 'Adjust the heading time to match the visuals (e.g. change DAY to DUSK or NIGHT).',
        severity: 'error'
    },
    'FRM-16': {
        code: 'FRM-16',
        title: 'Incorrect Use of CONTINUOUS',
        description: 'The CONTINUOUS marker should only be used if the scene occurs immediately after the previous one. It cannot be used in the first scene.',
        fixPrompt: 'Remove "CONTINUOUS" or replace with a real time (DAY/NIGHT) if this is the first scene or if there is a time jump.',
        severity: 'suggestion'
    },
    'FRM-17': {
        code: 'FRM-17',
        title: 'Incorrect Script Finishing',
        description: 'The script ends abruptly or uses internal transitions (like CUT TO:) to finish. Industry standards require a formal closure.',
        fixPrompt: 'To close the project "lid": skip two lines after the final speech and insert FADE OUT. (right-aligned) or THE END. (centered). Remove any "CUT TO:" at the end.',
        severity: 'error'
    },
    'FRM-18': {
        code: 'FRM-18',
        title: 'Orphan Slugline (End of Page)',
        description: 'A scene heading appears alone at the bottom of a page. Headings must have at least one line of action or dialogue below them on the same page.',
        fixPrompt: 'Add a Page Break before the scene heading to move it to the top of the next page.',
        severity: 'error'
    },
    'FRM-19': {
        code: 'FRM-19',
        title: 'Markdown Residue and IT Artifacts',
        description: 'The text contains Markdown formatting residue (asterisks *, underscores _, hashes #) or IT code artifacts (like triple backticks ```) which are illegal in a professional screenplay. These do not belong in cinema.',
        fixPrompt: 'PROVIDE CLEAN TEXT WITHOUT MARKDOWN. Remove ```, *, _, #. If text exists after FADE OUT/THE END, delete it. Plain text only.',
        severity: 'error'
    },
    'FRM-20': {
        code: 'FRM-20',
        title: 'False Dialogue (Action Formatted as Speech)',
        description: 'Detected a centered text block (Dialogue format) whose content is purely descriptive, narrative, or visual (Action). This often occurs when AI mistakes a character introduction for the start of speech.',
        fixPrompt: 'IMMEDIATELY convert this block to ACTION format (Left-Aligned). If there is a character name above (CUE), delete it and integrate the name into the first sentence of the action (e.g., Change MARCOS [break] He runs. to Marcos runs.).',
        severity: 'error'
    },
    'ACT-01': {
        code: 'ACT-01',
        title: 'Action Block Too Long',
        description: 'Description paragraphs should have a maximum of 4 to 5 lines.',
        fixPrompt: 'Break the action block into smaller paragraphs of at most 4 lines.',
        severity: 'suggestion'
    },
    'ACT-02': {
        code: 'ACT-02',
        title: 'Camera Direction in Spec Script',
        description: 'Avoid technical camera terms like CLOSE ON, ANGLE ON or CAMERA TRACKS in the description. Standard transitions like "CUT TO:" are allowed.',
        fixPrompt: 'REWRITE ACTION WITHOUT CAMERA TERMS. Describe only the visual. Do not explain.',
        severity: 'error'
    },
    'ACT-03': {
        code: 'ACT-03',
        title: 'Internal State/Psychology (Unfilmable)',
        description: 'Description should show only what the camera records. Avoid describing what the character "feels", "thinks", "knows" or "realizes".',
        fixPrompt: 'TRANSFORM INTO BEHAVIOR: NEVER just remove the emotion. EXTERNALIZE the abstract feeling into a physical action or reaction. Ex: Instead of "in shock", use "Her antennas tremble and she takes a step back". Instead of "He is exhausted", use "He collapses to the floor, panting".',
        severity: 'error'
    },
    'ACT-04': {
        code: 'ACT-04',
        title: 'Psychology/Emotion in Action (Show, Don\'t Tell)',
        description: 'Use of purely abstract or internal terms (e.g. "She feels fear", "Determination in her eyes"). Visible physical manifestations (e.g. "Cries", "Shouts", "Laughs") are allowed and recommended.',
        fixPrompt: 'TRANSFORM INTO BEHAVIOR: Replace the abstract feeling with a unique physical action. If nervous, use "Their hands shake".',
        severity: 'error'
    },
    'ACT-05': {
        code: 'ACT-05',
        title: 'Exposition/Explanation in Action',
        description: 'The action block explains biography, history or internal feelings that the camera does not see.',
        fixPrompt: 'Rewrite abstract descriptions to be visual. Keep the action, remove invisible explanations.',
        severity: 'suggestion'
    },
    'ACT-10': {
        code: 'ACT-10',
        title: 'AI Junk / Metadata',
        description: 'The script contains AI notes, greetings, or technical explanations outside the format.',
        fixPrompt: 'Remove only AI notes or greetings. KEEP ALL NARRATIVE CONTENT OF THE SCRIPT.',
        severity: 'error'
    },
    'ACT-11': {
        code: 'ACT-11',
        title: 'Non-Standard Legend Formatting',
        description: 'Use of terms like "LEGEND:" or invented formats for on-screen text.',
        fixPrompt: 'Replace "LEGEND:" or similar with the professional standard "TITLE ON SCREEN:" or "SUPER:".',
        severity: 'error'
    },
    'ACT-12': {
        code: 'ACT-12',
        title: 'Literary/Non-Visual Language (Sight & Sound Only)',
        description: 'Use of poetic metaphors or unfilmable senses IN THE SCENE DESCRIPTION (Action).',
        fixPrompt: 'Replace the literary with the visual/auditory. Scripts require objective description of the NOW.',
        severity: 'error'
    },
    'ACT-13': {
        code: 'ACT-13',
        title: 'Novelization',
        description: 'Text that narrates events using an omniscient narrator voice IN THE ACTION.',
        fixPrompt: 'Transform the narrative summary into present, raw actions.',
        severity: 'error'
    },
    'ACT-14': {
        code: 'ACT-14',
        title: 'Sound/FX Capitalization',
        description: 'Important sounds and crucial visual effects should be capitalized (ALL CAPS) on their first appearance in the action. NOTE: Characters (living beings/entities) are NOT SFX.',
        fixPrompt: 'Capitalize only the name of the sound or important visual effect mentioned (e.g. A SHOT RINGS OUT). Already introduced character names remain in lowercase.',
        severity: 'suggestion'
    },
    'ACT-15': {
        code: 'ACT-15',
        title: 'Excessive Adjectives and Judgments',
        description: 'Use of subjective adjectives or inferred evaluations.',
        fixPrompt: 'MAXIMUM OBJECTIVITY: Replace adjectives with measurements or physical facts whenever possible.',
        severity: 'suggestion'
    },
    'ACT-16': {
        code: 'ACT-16',
        title: 'Excessive Capitalization in Action',
        description: 'Common objects and generic locations should not be in ALL CAPS in the action to give literary emphasis.',
        fixPrompt: 'Convert to lowercase. Keep ALL CAPS only for Characters (1st time), Important Sounds, and Visual Effects.',
        severity: 'suggestion'
    },
    'ACT-17': {
        code: 'ACT-17',
        title: 'Non-Visual Sensations in Action',
        description: 'The script should describe only what can be seen or heard. Internal sensations are not filmable.',
        fixPrompt: 'Replace sensations like "Strong smell" or "Memories return" with visual or auditory evidence.',
        severity: 'error'
    },
    'ACT-18': {
        code: 'ACT-18',
        title: 'Literary/Poetic Language',
        description: 'The script should not sound like a novel or poem. Avoid dramatic metaphors about the future or destiny.',
        fixPrompt: 'Replace literary phrases with concrete, physical descriptions of what is happening NOW.',
        severity: 'error'
    },
    'ACT-19': {
        code: 'ACT-19',
        title: 'Literary Construction (Prose)',
        description: 'Terms like "Suddenly" are literary prose constructions. The script must describe action directly.',
        fixPrompt: 'Remove "Suddenly" and describe the action directly.',
        severity: 'suggestion'
    },
    'ACT-20': {
        code: 'ACT-20',
        title: 'Technical Image Description',
        description: 'Spec Scripts (Master Scene) do not describe image color, film texture, or visual memory treatments.',
        fixPrompt: 'Remove descriptions of color, texture, or image treatment. Focus exclusively on observable physical action.',
        severity: 'suggestion'
    },
    'ACT-21': {
        code: 'ACT-21',
        title: 'Narrative Camera Direction (Seen from...)',
        description: 'Avoid describing camera angles like "Seen from above", "Aerial", or "Point of view". Focus on the action, not the lens.',
        fixPrompt: 'Rewrite focusing on action. From: "Seen from above: Swimmers..." To: "Swimmers form a line in the water...".',
        severity: 'suggestion'
    },
    'ACT-22': {
        code: 'ACT-22',
        title: 'Meta-Description or Scene Summary',
        description: 'The text describes that "we see a scene" or "a sequence of..." instead of describing the direct action. The script must show the event, not summarize its existence.',
        fixPrompt: 'TRANSFORM INTO DIRECT ACTION: Instead of "We see scenes of children playing", describe "Children jump rope and run". Remove "Scenes of...", "Sequence of..." or "We see".',
        severity: 'error'
    },
    'DLG-01': {
        code: 'DLG-01',
        title: 'Expository Dialogue (On-the-nose)',
        description: 'The character says exactly what they feel or explains the plot artificially.',
        fixPrompt: 'Refine the dialogue to use subtext.',
        severity: 'suggestion'
    },
    'DLG-02': {
        code: 'DLG-02',
        title: 'V.O. / Narration Formatted as Action',
        description: 'Narration indications or Voice Over lines should not be in description blocks.',
        fixPrompt: 'Format properly as Dialogue: NAME (V.O.) followed by the speech text.',
        severity: 'error'
    },
    'DLG-03': {
        code: 'DLG-03',
        title: 'Action Inside Dialogue (The Break Rule)',
        description: 'It is STRICTLY PROHIBITED to describe physical actions or scene narration inside a dialogue block. This includes cases where the entire block is a third-person narrative description (action) positioned as speech. CAUTION: If a character just MENTIONS an action or object in speech (e.g., "I will leave now"), this is legitimate DIALOGUE and NOT an error.',
        fixPrompt: 'APPLY THE BREAK RULE: Extract the action to a new Action paragraph. If the block describes a sound (e.g., growling, laughing, screaming), you MUST "heal" the speech by inserting an appropriate onomatopoeia (e.g., "Grrrrrrr", "Hahaha", "Aaaaah!") consistent with the parenthetical, keeping the speech block active, and THEN move the action below.',
        severity: 'error'
    },
    'DLG-04': {
        code: 'DLG-04',
        title: 'Interpretive/Emotional Parenthetical',
        description: 'Parentheticals must indicate ONLY INTENTION or TONE OF VOICE (e.g. whispers, sarcastic). Max 3 words. PROHIBITED: physical actions. PROHIBITED: line breaks or hyphenation. MANDATORY: Text must be in lowercase (e.g., "whispers"). NO SPACES: Never use spaces before or inside (e.g., " (text)" or "( text )"). THE PARENTHETICAL MUST BE ALONE ON ITS OWN LINE.',
        fixPrompt: 'Remove physical action. If it is a visible action or has more than 3 words, move it to an Action line. Convert text to lowercase. Keep in parentheses only the tone of voice. REMOVE SPACES: Ensure the parenthesis/bracket is snug against the text and there are no leading spaces on the line.',
        severity: 'error'
    },
    'DLG-06': {
        code: 'DLG-06',
        title: 'Dialogue on Same Line as Parenthetical',
        description: 'Dialogue must never start on the same line as the parenthetical. The parenthetical must occupy its own line.',
        fixPrompt: 'Add a line break after the closing parenthesis to move the dialogue to the next line.',
        severity: 'error'
    },
    'DLG-07': {
        code: 'DLG-07',
        title: 'Word-breaking in Dialogue',
        description: 'Words must not be hyphenated or broken across lines within the dialogue block. The word should be kept whole on the current line or moved entirely to the next.',
        fixPrompt: 'Remove word-breaking hyphens and ensure the term is whole on a single line.',
        severity: 'error'
    },
    'DLG-05': {
        code: 'DLG-05',
        title: 'Irregular Capitalization in Dialogue',
        description: 'Use of uppercase letters in the middle of sentences without justification.',
        fixPrompt: 'Correct capitalization to follow standard grammar rules.',
        severity: 'suggestion'
    },
    'DLG-08': {
        code: 'DLG-08',
        title: 'Transcription Echo (Duplicated Dialogue)',
        description: 'The exact dialogue text was repeated below as an action line, or the entire dialogue is improperly in ALL CAPS.',
        fixPrompt: 'Remove the duplicate action line and convert the dialogue text to normal sentence case (only the first letter capitalized).',
        severity: 'error'
    },
    'CHR-01': {
        code: 'CHR-01',
        title: 'Non-Standard Character Introduction',
        description: 'Incorrect use of narrative concepts instead of visual description in the introduction.',
        fixPrompt: 'Adjust to: NAME (TYPE/AGE), visual description.',
        severity: 'error'
    },
    'CHR-02': {
        code: 'CHR-02',
        title: 'Character Name in Lowercase',
        description: 'The character name above the dialogue must be in ALL CAPS.',
        fixPrompt: 'Put the character name in ALL CAPS.',
        severity: 'error'
    },
    'CHR-03': {
        code: 'CHR-03',
        title: 'First Appearance of Character',
        description: 'The first time a character is introduced in the Action, their name must be in ALL CAPS. CAUTION: Characters are NOT SFX.',
        fixPrompt: 'Capitalize the character name in this first appearance.',
        severity: 'error'
    },
    'CHR-04': {
        code: 'CHR-04',
        title: 'Ghost Character (Disembodied Voice)',
        description: 'A character has a line (DIALOGUE) but was not introduced or mentioned in the action immediately before.',
        fixPrompt: 'Insert an action line before the dialogue describing the character or their entrance (e.g. "A DIVER adjusts his goggles.").',
        severity: 'error'
    },
    'LOG-01': {
        code: 'LOG-01',
        title: 'Object Permanence Error',
        description: 'An object appears or disappears without logical explanation or prior mention.',
        fixPrompt: 'Add a previous action where the character picks up/carries the object or remove the object use if it shouldn\'t be there.',
        severity: 'error'
    },
    'LOG-02': {
        code: 'LOG-02',
        title: 'Physically Impossible Action',
        description: 'The character performs an action that contradicts the physics of the setting or their previous position.',
        fixPrompt: 'Adjust the action to be physically possible within the established spatial context.',
        severity: 'error'
    },
    'LOG-03': {
        code: 'LOG-03',
        title: 'Impossible Setting (INT./EXT.)',
        description: 'The heading defines "INT." for a naturally open location (e.g. SEA, SKY, FOREST) without containment. Open water is always EXT.',
        fixPrompt: 'Change to EXT. if outdoors. For underwater, use "EXT. SEA - UNDERWATER". Use INT. only if INSIDE a constructed structure.',
        severity: 'error'
    },
    'STR-01': {
        code: 'STR-01',
        title: 'Absence of Value Charge',
        description: 'The scene ends in the same emotional or situational state it started. A scene must turn the value (e.g. from Positive to Negative).',
        fixPrompt: 'Introduce a conflict or revelation that changes the scene\'s charge.',
        severity: 'warning'
    },
    'STR-02': {
        code: 'STR-02',
        title: 'Lack of Character Goal',
        description: 'The protagonist of the scene does not seem to have a clear goal or immediate dramatic need.',
        fixPrompt: 'Establish what the character wants to achieve in this specific scene.',
        severity: 'warning'
    },
    'STR-03': {
        code: 'STR-03',
        title: 'Absence of Conflict/Antagonism',
        description: 'The scene progresses without resistance. Characters agree too easily or there are no opposing forces.',
        fixPrompt: 'Introduce obstacles, whether internal (doubt), interpersonal (another character), or environmental.',
        severity: 'warning'
    },
    'STR-04': {
        code: 'STR-04',
        title: 'Static Pacing',
        description: 'The scene consists only of dialogue or only of repetitive action, without narrative progression.',
        fixPrompt: 'Vary the pacing. Intersperse meaningful dialogue with action beats that move the story forward.',
        severity: 'warning'
    },
    'HDR-01': {
        code: 'HDR-01',
        title: 'Inconsistent Scene Heading Name',
        description: 'The same location must be named identically in all headings to avoid production breakdown errors.',
        fixPrompt: 'Standardize the name of this scene using the term previously established in the script.',
        severity: 'warning'
    },
    'ACT-23': {
        code: 'ACT-23',
        title: 'Technical Highlight (SFX/Props)',
        description: 'Important sound effects (SFX) and props must be capitalized (ALL CAPS) on their first appearance.',
        fixPrompt: 'Put the technical term or key object in ALL CAPS.',
        severity: 'suggestion'
    },
    'STY-01': {
        code: 'STY-01',
        title: 'Spec Script Cleanup',
        description: 'Remove side scene numbers and redundant transitions (e.g. CUT TO:) that serve no specific dramatic function.',
        fixPrompt: 'Delete scene numbers and clean up unnecessary transitions for the Master Scene standard.',
        severity: 'error'
    },
};

export const AUDIT_ERRORS_ES: Record<AuditErrorKey, AuditErrorDefinition> = {
    'FRM-01': {
        code: 'FRM-01',
        title: 'Encabezado de Escena Incorrecto',
        description: 'El encabezado debe incluir obligatoriamente el tiempo de luz (DÍA, NOCHE, etc). Términos como "CONTÍNUO" no sustituyen la iluminación principal.',
        fixPrompt: 'Ajuste a: INT./EXT. LOCAL (EN MAYÚSCULAS) - [DÍA/NOCHE] - CONTÍNUO. Todo el encabezado debe estar obligatoriamente en MAYÚSCULAS.',
        severity: 'error'
    },
    'FRM-02': {
        code: 'FRM-02',
        title: 'Espaciado de Personajes (Glue Rule)',
        description: 'Existe un espacio ilegal entre el nombre del personaje y el diálogo.',
        fixPrompt: 'Elimine la línea en blanco entre el nombre del personaje y el comienzo del diálogo.',
        severity: 'error'
    },
    'FRM-03': {
        code: 'FRM-03',
        title: 'Etiqueta de Título Innecesaria',
        description: 'El título del guion contiene la etiqueta "TITLE:" o "TÍTULO:".',
        fixPrompt: 'Elimine el prefijo "TITLE:" o "TÍTULO:". El título debe ser solo el texto en MAYÚSCULAS.',
        severity: 'error'
    },
    'FRM-04': {
        code: 'FRM-04',
        title: 'Jerarquía de Ubicación Invertida',
        description: 'El encabezado describe la sububicación antes de la ubicación principal.',
        fixPrompt: 'Invierta el orden a [UBICACIÓN PRINCIPAL] – [SUBUBICACIÓN] – [PERÍODO]. Use siempre MAYÚSCULAS.',
        severity: 'error'
    },
    'FRM-05': {
        code: 'FRM-05',
        title: 'Formato de Flashback',
        description: 'Aunque el uso de FLASHBACK al comienzo del encabezado es aceptable, el estándar más común en la industria es colocarlo al final de la línea.',
        fixPrompt: 'Mueva la etiqueta al final del encabezado. Ej: INT. UBICACIÓN - DÍA - FLASHBACK.',
        severity: 'suggestion'
    },
    'FRM-06': {
        code: 'FRM-06',
        title: 'Nombres de Personajes Duplicados',
        description: 'El nombre del personaje aparece repetido dos veces seguidas sin diálogo entre ellos.',
        fixPrompt: 'Elimine el nombre duplicado y asegúrese de que solo haya una línea de personaje antes del diálogo.',
        severity: 'error'
    },
    'FRM-07': {
        code: 'FRM-07',
        title: 'Marcadores de Capítulo/Numeración',
        description: 'El guion contiene divisiones de capítulos o numeración de escenas/transiciones, lo cual no se recomienda en Guiones Especulativos (Spec Scripts).',
        fixPrompt: 'Elimine los marcadores de capítulo y la numeración de escenas/transiciones.',
        severity: 'suggestion'
    },
    'FRM-08': {
        code: 'FRM-08',
        title: 'Uso Incorrecto de FIN/THE END',
        description: 'El término centrado "FIN" o "THE END" se usó fuera del cierre absoluto del guion.',
        fixPrompt: 'Mantenga "FIN" solo al final absoluto. Para transiciones, use términos estándar como "CORTE A:".',
        severity: 'error'
    },
    'FRM-09': {
        code: 'FRM-09',
        title: 'Formato de Contacto Mal Formado',
        description: 'El correo electrónico y el número de teléfono de contacto deben estar en líneas separadas en la esquina inferior izquierda de la portada.',
        fixPrompt: 'Separe el correo electrónico y el teléfono en dos líneas distintas.',
        severity: 'suggestion'
    },
    'FRM-10': {
        code: 'FRM-10',
        title: 'Título no está en Mayúsculas',
        description: 'El título del guion en la portada debe estar siempre en MAYÚSCULAS.',
        fixPrompt: 'Convierta el título del guion a MAYÚSCULAS.',
        severity: 'error'
    },
    'FRM-11': {
        code: 'FRM-11',
        title: 'Error en la Línea de Crédito',
        description: 'La etiqueta "Escrito por" y el nombre del autor deben estar en la misma línea.',
        fixPrompt: 'Una "Escrito por" y el nombre del autor en la misma línea.',
        severity: 'error'
    },
    'FRM-12': {
        code: 'FRM-12',
        title: 'Error en la Etiqueta "Basado en,"',
        description: 'La etiqueta "Basado en," requiere el uso de la coma y debe estar centrada.',
        fixPrompt: 'Ajuste a "Basado en," con la coma obligatoria.',
        severity: 'error'
    },
    'FRM-13': {
        code: 'FRM-13',
        title: 'Encabezado no está en Mayúsculas',
        description: 'Todos los elementos del encabezado (Ubicación, Sububicación, Tiempo e indicadores) deben estar en MAYÚSCULAS.',
        fixPrompt: 'Convierta todo el encabezado a MAYÚSCULAS.',
        severity: 'error'
    },
    'FRM-14': {
        code: 'FRM-14',
        title: 'Extensión de Personaje Aislada',
        description: 'Las extensiones como (V.O.), (O.S.) o (CONT\'D) deben estar obligatoriamente en la misma línea que el nombre del personaje.',
        fixPrompt: 'Una la extensión al nombre del personaje en la misma línea. Ej: "JUAN (V.O.)"',
        severity: 'error'
    },
    'FRM-15': {
        code: 'FRM-15',
        title: 'Inconsistencia de Tiempo/Luz',
        description: 'El tiempo indicado en el encabezado (ej: DÍA) contradice la descripción de la escena (ej: "el sol se pone").',
        fixPrompt: 'Ajuste el tiempo en el encabezado para que coincida con lo visual (ej: cambie de DÍA a ATARDECER o NOCHE).',
        severity: 'error'
    },
    'FRM-16': {
        code: 'FRM-16',
        title: 'Uso Incorrecto de CONTÍNUO',
        description: 'El marcador CONTÍNUO solo debe usarse si la escena ocurre inmediatamente después de la anterior. No se puede usar en la primera escena.',
        fixPrompt: 'Elimine "CONTÍNUO" o reemplácelo con un tiempo real (DÍA/NOCHE) si esta es la primera escena o si hay un salto temporal.',
        severity: 'suggestion'
    },
    'FRM-17': {
        code: 'FRM-17',
        title: 'Transición de Cierre Incorrecta',
        description: 'La transición "CORTE A:" (o "CUT TO:") es una herramienta de edición interna entre escenas y no debe usarse para finalizar un guión.',
        fixPrompt: 'Para cerrar la "tapa" del proyecto: salta dos líneas después del último diálogo e inserta un FADE OUT. (alineado a la derecha) o un FIN. (centrado). Elimine cualquier "CORTE A:" al final.',
        severity: 'error'
    },
    'FRM-18': {
        code: 'FRM-18',
        title: 'Encabezado Huérfano (Final de Página)',
        description: 'Un encabezado de escena aparece solo al final de una página. Los encabezados deben tener al menos una línea de acción o diálogo debajo de ellos en la misma página.',
        fixPrompt: 'Agregue un Salto de Página antes del encabezado de escena para moverlo al principio de la siguiente página.',
        severity: 'error'
    },
    'FRM-19': {
        code: 'FRM-19',
        title: 'Residuo de Markdown y Artefactos',
        description: 'El texto contiene residuos de formato Markdown (asteriscos *, guiones bajos _, almohadillas # o comillas invertidas `) o artefactos de código (como ``` o ...) que son ilegales. Incluye marcas después del FADE OUT.',
        fixPrompt: 'PROPORCIONE EL TEXTO LIMPIO SIN MARKDOWN. Elimine ```, *, _, #. Si hay texto tras FIN/FADE OUT, bórrelo. Solo texto plano.',
        severity: 'error'
    },
    'FRM-20': {
        code: 'FRM-20',
        title: 'Falso Diálogo (Acción Formateada como Diálogo)',
        description: 'Se ha detectado un bloque de texto centrado (formato de Diálogo) cuyo contenido es puramente descriptivo, narrativo o visual (Acción). Ocurre a menudo cuando la IA confunde la presentación de un personaje con el inicio de un diálogo.',
        fixPrompt: 'Convierta INMEDIATAMENTE este bloque al formato de ACCIÓN (Alineado a la Izquierda). Si hay un nombre de personaje encima (CUE), elimínelo e integre el nombre en la primera frase de la acción (Ej: De MARCOS [salto] Él corre. a Marcos corre.).',
        severity: 'error'
    },
    'ACT-01': {
        code: 'ACT-01',
        title: 'Bloque de Acción Muy Largo',
        description: 'Los párrafos de descripción deben tener un máximo de 4 a 5 líneas.',
        fixPrompt: 'Divida el bloque de acción en párrafos más pequeños de máximo 4 líneas.',
        severity: 'suggestion'
    },
    'ACT-02': {
        code: 'ACT-02',
        title: 'Dirección de Cámara en Spec Script',
        description: 'Evite términos técnicos de cámara como CLOSE ON, ANGLE ON o LA CÁMARA ACOMPAÑA en la descripción. Se permiten transiciones estándar como "CORTE A:".',
        fixPrompt: 'REESCRIBA LA ACCIÓN SIN TÉRMINOS TÉCNICOS. Describa solo lo visual. No explique.',
        severity: 'error'
    },
    'ACT-03': {
        code: 'ACT-03',
        title: 'Estado Interno/Psicología (No Filmable)',
        description: 'La descripción debe mostrar solo lo que la cámara graba. Evita describir lo que el personaje "siente", "piensa", "sabe" o "se da cuenta".',
        fixPrompt: 'TRANSFORMAR EN COMPORTAMIENTO: NUNCA solo remuevas la emoción. EXTERNALIZA el sentimiento abstracto en una acción o reacción física. Ej: En lugar de "en shock", usa "Sus antenas tiemblan y da un paso atrás". En lugar de "Él está exhausto", usa "Se desploma en el suelo, jadeando".',
        severity: 'error'
    },
    'ACT-04': {
        code: 'ACT-04',
        title: 'Psicología/Emoción en la Acción (Muestra, No Cuentes)',
        description: 'Uso de términos puramente abstractos o internos (ej: "Ella siente miedo", "Determinación en sus ojos"). Se permiten y recomiendan manifestaciones físicas visibles (ej: "Llora", "Grita", "Ríe").',
        fixPrompt: 'TRANSFORME EN COMPORTAMIENTO: Sustituya el sentimiento abstracto por una acción física única. Si está nervioso, use "Sus manos tiemblan".',
        severity: 'error'
    },
    'ACT-05': {
        code: 'ACT-05',
        title: 'Exposición/Explicación en la Acción',
        description: 'El bloque de acción explica biografía, historia o sentimientos internos que la cámara no ve.',
        fixPrompt: 'Reescriba las descripciones abstractas para que sean visuales. Mantenga la acción, elimine solo explicaciones invisibles.',
        severity: 'suggestion'
    },
    'ACT-10': {
        code: 'ACT-10',
        title: 'Basura de IA / Metadatos',
        description: 'El guion contiene notas de la IA, saludos o explicaciones técnicas fuera del formato.',
        fixPrompt: 'Elimine solo notas de IA o saludos. MANTENGA TODO EL CONTENIDO NARRATIVO DEL GUION.',
        severity: 'error'
    },
    'ACT-11': {
        code: 'ACT-11',
        title: 'Formato de Leyenda no estándar',
        description: 'Uso de términos como "LEGEND:" o formatos inventados para textos en pantalla.',
        fixPrompt: 'Reemplace "LEGEND:" o similares por el estándar profesional "TITLE ON SCREEN:" o "SUPER:".',
        severity: 'error'
    },
    'ACT-12': {
        code: 'ACT-12',
        title: 'Lenguaje Literario/No Visual',
        description: 'Uso de metáforas poéticas o sentidos no filmables en la descripción de la escena.',
        fixPrompt: 'Sustituya lo literario por lo visual/auditivo. Los guiones requieren una descripción objetiva del AHORA.',
        severity: 'error'
    },
    'ACT-13': {
        code: 'ACT-13',
        title: 'Novelización',
        description: 'Texto que narra eventos usando una voz de narrador omnisciente EN LA ACCIÓN.',
        fixPrompt: 'Transforme el resumen narrativo en acciones presentes y crudas.',
        severity: 'error'
    },
    'ACT-14': {
        code: 'ACT-14',
        title: 'Capitalización de Sonido/Efectos',
        description: 'Los sonidos importantes y los efectos visuales cruciales deben estar capitalizados (MAYÚSCULAS) en su primera aparición en la acción.',
        fixPrompt: 'Ponga en MAYÚSCULAS solo el nombre del sonido o efecto visual importante mencionado (ej: UN DISPARO RESUENA).',
        severity: 'suggestion'
    },
    'ACT-15': {
        code: 'ACT-15',
        title: 'Exceso de Adjetivos y Juicios',
        description: 'Uso de adjetivos subjetivos o evaluaciones inferidas.',
        fixPrompt: 'MÁXIMA OBJETIVIDAD: Reemplace adjetivos con medidas o hechos físicos siempre que sea posible.',
        severity: 'suggestion'
    },
    'ACT-16': {
        code: 'ACT-16',
        title: 'Capitalización Excesiva en la Acción',
        description: 'Los objetos comunes y lugares genéricos no deben estar en MAYÚSCULAS en la acción para dar énfasis literario.',
        fixPrompt: 'Convierta a minúsculas. Mantenga MAYÚSCULAS solo para Personajes (1ª vez), Sonidos Importantes y Efectos Visuales.',
        severity: 'suggestion'
    },
    'ACT-17': {
        code: 'ACT-17',
        title: 'Sensaciones No Visuales en la Acción',
        description: 'El guion debe describir solo lo que se puede ver o escuchar. Las sensaciones internas no son filmables.',
        fixPrompt: 'Reemplace sensaciones como "Olor fuerte" o "Recuerdos vuelven" con evidencia visual o auditiva.',
        severity: 'error'
    },
    'ACT-18': {
        code: 'ACT-18',
        title: 'Lenguaje Literario/Poético',
        description: 'El guion no debe sonar como una novela o poema. Evite metáforas dramáticas sobre el futuro o el destino.',
        fixPrompt: 'Reemplace frases literarias con descripciones concretas y físicas de lo que está sucediendo AHORA.',
        severity: 'error'
    },
    'ACT-19': {
        code: 'ACT-19',
        title: 'Construcción Literaria (Prosa)',
        description: 'Términos como "De repente" son construcciones literarias de prosa. El guion debe describir la acción directamente.',
        fixPrompt: 'Elimine "De repente" y describa la acción directamente.',
        severity: 'suggestion'
    },
    'ACT-20': {
        code: 'ACT-20',
        title: 'Descripción Técnica de Imagen',
        description: 'Los Guiones Especulativos (Master Scene) no describen color de imagen, textura de película o tratamientos visuales de memoria.',
        fixPrompt: 'Elimine descripciones de color, textura o tratamiento de imagen. Enfóquese exclusivamente en la acción física observable.',
        severity: 'suggestion'
    },
    'ACT-21': {
        code: 'ACT-21',
        title: 'Dirección de Cámara Narrativa (Visto desde...)',
        description: 'Evite describir ángulos de cámara como "Visto desde arriba", "Aérea" o "Punto de vista". Enfóquese en la acción, no en la lente.',
        fixPrompt: 'Reescriba enfocándose en la acción. De: "Visto desde arriba: Nadadores..." A: "Los nadadores forman una línea en el agua...".',
        severity: 'suggestion'
    },
    'ACT-22': {
        code: 'ACT-22',
        title: 'Meta-Descripción o Resumen de Escena',
        description: 'El texto describe que "vemos una escena" o "una secuencia de..." en lugar de describir la acción directa. El guion debe mostrar el evento, no resumir su existencia.',
        fixPrompt: 'TRANSFORME EN ACCIÓN DIRECTA: En lugar de "Vemos escenas de niños jugando", describa "Niños saltan la cuerda y corren". Elimine "Escenas de...", "Secuencia de..." o "Vemos".',
        severity: 'error'
    },
    'DLG-01': {
        code: 'DLG-01',
        title: 'Diálogo Expositivo (On-the-nose)',
        description: 'El personaje dice exactamente lo que siente o explica la trama de forma artificial.',
        fixPrompt: 'Refine el diálogo para usar subtexto.',
        severity: 'suggestion'
    },
    'DLG-02': {
        code: 'DLG-02',
        title: 'V.O. / Narración Formateada como Acción',
        description: 'Indicaciones de narración o líneas de Voice Over no deben estar en bloques de descripción.',
        fixPrompt: 'Formatee adecuadamente como Diálogo: NOMBRE (V.O.) seguido del texto del discurso.',
        severity: 'error'
    },
    'DLG-03': {
        code: 'DLG-03',
        title: 'Acción Dentro del Diálogo (Regla de la Ruptura)',
        description: 'Está ESTRICTAMENTE PROHIBIDO describir acciones físicas o narración de escena dentro de un bloque de diálogo. Esto incluye casos donde el bloque entero es una descripción narrativa en tercera persona (acción) posicionada como habla. ATENCIÓN: Si el personaje solo MENCIONA una acción u objeto (ej: "Voy a salir ahora"), esto es DIÁLOGO legítimo y NO debe marcarse como error.',
        fixPrompt: 'APLIQUE LA LEY DE QUIEBRE: Extraiga la acción a un nuevo párrafo de Acción. Si el bloque describe un sonido (ej: gruñir, reír, gritar), DEBES "curar" el habla insertando una onomatepeya adecuada (ej: "Grrrrrrr", "Hahaha", "¡Aaaaah!") coherente con la rúbrica, manteniendo el bloque de habla activo, y LUEGO mueva la acción hacia abajo.',
        severity: 'error'
    },
    'DLG-04': {
        code: 'DLG-04',
        title: 'Rúbrica Interpretativa/Emocional',
        description: 'Las rúbricas deben indicar solo INTENCIÓN o TONO DE VOZ (ej: susurra, sarcástico). Máximo 3 palabras. PROHIBIDO: acciones físicas. PROHIBIDO: saltos de línea o hifenización. OBLIGATORIO: Texto de la rúbrica en letras minúsculas (ej: "susurra"). SIN ESPACIOS: Jamás use espacios antes o dentro (ej: " (texto)" ou "( texto )"). LA RÚBRICA DEBE ESTAR SOLA EN SU PROPIA LÍNEA.',
        fixPrompt: 'Elimine la acción física. Si es una acción visible o tiene más de 3 palabras, muévala a una línea de Acción. Convierta el texto a minúsculas. Mantenga en los paréntesis solo el tono de voz. ELIMINE ESPACIOS: Asegúrese de que el paréntesis/corchete esté pegado al texto y no haya espacios al inicio de la línea.',
        severity: 'error'
    },
    'DLG-06': {
        code: 'DLG-06',
        title: 'Diálogo en la Misma Línea que la Rúbrica',
        description: 'El diálogo nunca puede comenzar en la mesma línea que la rúbrica (paréntesis). La rúbrica debe estar sola en su propia línea.',
        fixPrompt: 'Agregue un salto de línea después del paréntesis final para mover el diálogo a la línea de abajo.',
        severity: 'error'
    },
    'DLG-07': {
        code: 'DLG-07',
        title: 'Guionización en el Cuerpo del Diálogo',
        description: 'Las palabras no deben dividirse con guiones ni romperse entre líneas en el bloque de diálogo. La palabra debe mantenerse íntegra en la línea actual o moverse por completo a la siguiente.',
        fixPrompt: 'Elimine los guiones de división de palabras y asegúrese de que el término esté completo en una sola línea.',
        severity: 'error'
    },
    'DLG-05': {
        code: 'DLG-05',
        title: 'Capitalización Irregular en Diálogo',
        description: 'Uso de letras mayúsculas en medio de oraciones sin justificación.',
        fixPrompt: 'Corrija la capitalización para seguir las reglas gramaticales estándar.',
        severity: 'suggestion'
    },
    'DLG-08': {
        code: 'DLG-08',
        title: 'Eco de Transcripción (Diálogo Duplicado)',
        description: 'El texto exacto del diálogo se repitió debajo como una línea de acción, o todo el diálogo está indebidamente en MAYÚSCULAS.',
        fixPrompt: 'Elimine la línea de acción duplicada y convierta el texto del diálogo a formato de frase normal (seña la primera letra en mayúscula).',
        severity: 'error'
    },
    'CHR-01': {
        code: 'CHR-01',
        title: 'Introducción de Personaje No Estándar',
        description: 'Uso incorrecto de conceptos narrativos en lugar de descripción visual en la introducción.',
        fixPrompt: 'Ajuste a: NOMBRE (TIPO/EDAD), descripción visual.',
        severity: 'error'
    },
    'CHR-02': {
        code: 'CHR-02',
        title: 'Nombre de Personaje en Minúscula',
        description: 'El nombre del personaje sobre el diálogo debe estar en MAYÚSCULAS.',
        fixPrompt: 'Ponga el nombre del personaje en MAYÚSCULAS.',
        severity: 'error'
    },
    'CHR-03': {
        code: 'CHR-03',
        title: 'Primera Aparición de Personaje',
        description: 'La primera vez que se presenta un personaje en la Acción, su nombre debe estar en MAYÚSCULAS. ATENCIÓN: Los personajes NO son SFX.',
        fixPrompt: 'Capitalice el nombre del personaje en esta primera aparición.',
        severity: 'error'
    },
    'CHR-04': {
        code: 'CHR-04',
        title: 'Personaje Fantasma (Voz sin Cuerpo)',
        description: 'Un personaje tiene una línea (DIÁLOGO) pero no fue introducido o mencionado en la acción inmediatamente anterior.',
        fixPrompt: 'Inserte una línea de acción antes del diálogo describiendo al personaje o su entrada (ej: "UN BUZO ajusta sus gafas.").',
        severity: 'error'
    },
    'LOG-01': {
        code: 'LOG-01',
        title: 'Error de Permanencia de Objeto',
        description: 'Un objeto aparece o desaparece sin explicación lógica o mención previa.',
        fixPrompt: 'Agregue una acción previa donde el personaje recoge/lleva el objeto o elimine el uso del objeto si no debería estar allí.',
        severity: 'error'
    },
    'LOG-02': {
        code: 'LOG-02',
        title: 'Acción Físicamente Imposible',
        description: 'El personaje realiza una acción que contradice la física del escenario o su posición anterior.',
        fixPrompt: 'Ajuste la acción para que sea físicamente posible dentro del contexto espacial establecido.',
        severity: 'error'
    },
    'LOG-03': {
        code: 'LOG-03',
        title: 'Escenario Imposible (INT./EXT.)',
        description: 'El encabezado define "INT." para un lugar naturalmente abierto (ej: MAR, CIELO) sin estar contenido. El mar siempre es EXT.',
        fixPrompt: 'Cambie a EXT. si es al aire libre. Para bajo el agua, use "EXT. MAR - BAJO EL AGUA". Use INT. solo si está DENTRO de una estructura.',
        severity: 'error'
    },
    'STR-01': {
        code: 'STR-01',
        title: 'Ausencia de Carga de Valor',
        description: 'La escena termina en el mismo estado emocional o situacional en que comenzó. Una escena debe girar el valor (ej: de Positivo a Negativo).',
        fixPrompt: 'Introduzca un conflicto o revelación que cambie la carga de la escena.',
        severity: 'warning'
    },
    'STR-02': {
        code: 'STR-02',
        title: 'Falta de Objetivo del Personaje',
        description: 'El protagonista de la escena no parece tener un objetivo claro o una necesidad dramática inmediata.',
        fixPrompt: 'Establezca qué quiere lograr el personaje en esta escena específica.',
        severity: 'warning'
    },
    'STR-03': {
        code: 'STR-03',
        title: 'Ausencia de Conflicto/Antagonismo',
        description: 'La escena progresa sin resistencia. Los personajes están de acuerdo demasiado fácilmente o no hay fuerzas opuestas.',
        fixPrompt: 'Introduzca obstáculos, ya sean internos (duda), interpersonales (otro personaje) o ambientales.',
        severity: 'warning'
    },
    'STR-04': {
        code: 'STR-04',
        title: 'Ritmo Estático',
        description: 'La escena consiste solo en diálogo o solo en acción repetitiva, sin progresión narrativa.',
        fixPrompt: 'Varíe el ritmo. Intercale diálogos significativos con momentos de acción que muevan la historia hacia adelante.',
        severity: 'warning'
    },
    'HDR-01': {
        code: 'HDR-01',
        title: 'Inconsistencia de Nombre de Escenario',
        description: 'El mismo lugar debe nombrarse de forma idéntica en todos los encabezados para evitar errores en el desglose de producción.',
        fixPrompt: 'Estandarice el nombre de este escenario utilizando el término ya establecido anteriormente en el guion.',
        severity: 'warning'
    },
    'ACT-23': {
        code: 'ACT-23',
        title: 'Destacado Técnico (SFX/Props)',
        description: 'Los efectos de sonido (SFX) y objetos de escena (Props) importantes deben estar en MAYÚSCULAS en su primera aparición.',
        fixPrompt: 'Ponga el término técnico o el objeto fundamental en MAYÚSCULAS.',
        severity: 'suggestion'
    },
    'STY-01': {
        code: 'STY-01',
        title: 'Limpieza de Spec Script',
        description: 'Elimine los números de escena laterales y las transiciones redundantes (ej: CORTE A:) que no tienen función dramática específica.',
        fixPrompt: 'Elimine los números de escena y limpie las transiciones innecesarias para el estándar Master Scene.',
        severity: 'error'
    },
};

export const AUDIT_ERRORS_FR: Record<AuditErrorKey, AuditErrorDefinition> = {
    'FRM-01': {
        code: 'FRM-01',
        title: 'En-tête de Scène Incorrect',
        description: 'L\'en-tête doit obligatoirement inclure le temps de lumière (JOUR, NUIT, etc). Les termes comme "CONTINU" ne remplacent pas l\'éclairage principal.',
        fixPrompt: 'Ajustez en : INT./EXT. LIEU (EN MAJUSCULES) - [JOUR/NUIT] - CONTINU. Tout l\'en-tête doit obligatoirement être en MAJUSCULES.',
        severity: 'error'
    },
    'FRM-02': {
        code: 'FRM-02',
        title: 'Espacement des Personnages (Glue Rule)',
        description: 'Il y a un espace illégal entre le nom du personnage et le dialogue.',
        fixPrompt: 'Supprimez la ligne vide entre le nom du personnage et le début de la réplique.',
        severity: 'error'
    },
    'FRM-03': {
        code: 'FRM-03',
        title: 'Étiquette de Titre Inutile',
        description: 'Le titre du scénario contient l\'étiquette "TITLE:" ou "TÍTULO:".',
        fixPrompt: 'Supprimez le préfixe "TITLE:" ou "TÍTULO:". Le titre doit être uniquement le texte en MAJUSCULES.',
        severity: 'error'
    },
    'FRM-04': {
        code: 'FRM-04',
        title: 'Hiérarchie de Lieu Inversée',
        description: 'L\'en-tête décrit le sous-lieu avant le lieu principal.',
        fixPrompt: 'Inversez l\'ordre en [LIEU PRINCIPAL] – [SOUS-LIEU] – [PÉRIODE]. Utilisez toujours des MAJUSCULES.',
        severity: 'error'
    },
    'FRM-05': {
        code: 'FRM-05',
        title: 'Formatage de Flashback',
        description: 'Bien que l\'utilisation de FLASHBACK au début de l\'en-tête soit acceptable, la norme la plus courante dans l\'industrie est de le placer à la fin de la ligne.',
        fixPrompt: 'Déplacez l\'étiquette à la fin de l\'en-tête. Ex : INT. LIEU - JOUR - FLASHBACK.',
        severity: 'suggestion'
    },
    'FRM-06': {
        code: 'FRM-06',
        title: 'Noms de Personnages Dupliqués',
        description: 'Le nom du personnage apparaît deux fois de suite sans dialogue entre les deux.',
        fixPrompt: 'Supprimez le nom en double et assurez-vous qu\'il n\'y a qu\'une seule ligne de personnage avant le dialogue.',
        severity: 'error'
    },
    'FRM-07': {
        code: 'FRM-07',
        title: 'Marqueurs de Chapitre/Numérotation',
        description: 'Le scénario contient des divisions de chapitres ou une numérotation de scènes/transitions, ce qui n\'est pas recommandé dans les Spec Scripts.',
        fixPrompt: 'Supprimez les mangeurs de chapitre et la numérotation des scènes/transitions.',
        severity: 'suggestion'
    },
    'FRM-08': {
        code: 'FRM-08',
        title: 'Utilisation Incorrecte de FIN/THE END',
        description: 'Le terme centré "FIN" ou "THE END" a été utilisé en dehors de la fin absolue du scénario.',
        fixPrompt: 'Gardez "FIN" uniquement à la toute fin. Pour les transitions, utilisez des termes standard comme "COUPE SUR :".',
        severity: 'error'
    },
    'FRM-09': {
        code: 'FRM-09',
        title: 'Format de Contact Mal Formé',
        description: 'L\'e-mail et le numéro de téléphone de contact doivent figurer sur des lignes distinctes en bas à gauche de la page de titre.',
        fixPrompt: 'Séparez l\'e-mail et le numéro de téléphone en deux lignes distinctes.',
        severity: 'suggestion'
    },
    'FRM-10': {
        code: 'FRM-10',
        title: 'Titre Pas en Majuscules',
        description: 'Le titre du scénario sur la page de titre doit toujours être en MAJUSCULES.',
        fixPrompt: 'Convertissez le titre du scénario en MAJUSCULES.',
        severity: 'error'
    },
    'FRM-11': {
        code: 'FRM-11',
        title: 'Erreur de Ligne de Crédit',
        description: 'L\'étiquette "Écrit par" et le nom de l\'auteur doivent être sur la même ligne.',
        fixPrompt: 'Joignez "Écrit par" et le nom de l\'auteur sur la même ligne.',
        severity: 'error'
    },
    'FRM-12': {
        code: 'FRM-12',
        title: 'Erreur d\'Étiquette "D\'après"',
        description: 'L\'étiquette "D\'après," nécessite une virgule et doit être centrée.',
        fixPrompt: 'Ajustez en "D\'après," avec la virgule obligatoire.',
        severity: 'error'
    },
    'FRM-13': {
        code: 'FRM-13',
        title: 'En-tête Pas en Majuscules',
        description: 'Tous les éléments de l\'en-tête (Lieu, Sous-lieu, Temps et indicateurs) doivent être en MAJUSCULES.',
        fixPrompt: 'Convertissez tout l\'en-tête en MAJUSCULES.',
        severity: 'error'
    },
    'FRM-14': {
        code: 'FRM-14',
        title: 'Extension de Personnage Isolée',
        description: 'Les extensions comme (V.O.), (O.S.) ou (SUITE) doivent obligatoirement être sur la même ligne que le nom du personnage.',
        fixPrompt: 'Joignez l\'extension au nom du personnage sur la même ligne. Ex : "JEAN (V.O.)"',
        severity: 'error'
    },
    'FRM-15': {
        code: 'FRM-15',
        title: 'Incohérence Temps/Lumière',
        description: 'Le temps indiqué dans l\'en-tête (ex : JOUR) contredit la description de la scène (ex : "le soleil se couche").',
        fixPrompt: 'Ajustez le temps dans l\'en-tête pour correspondre au visuel (ex : changez JOUR en CRÉPUSCULE ou NUIT).',
        severity: 'error'
    },
    'FRM-16': {
        code: 'FRM-16',
        title: 'Utilisation Incorrecte de CONTINU',
        description: 'Le marqueur CONTINU ne doit être utilisé que si la scène se produit immédiatement après la précédente. Il ne peut pas être utilisé dans la première scène.',
        fixPrompt: 'Supprimez "CONTINU" ou remplacez par un temps réel (JOUR/NUIT) si c\'est la première scène ou s\'il y a un saut temporel.',
        severity: 'suggestion'
    },
    'FRM-17': {
        code: 'FRM-17',
        title: 'Transition de Fin Incorrecte',
        description: 'La transition "COUPE SUR :" (ou "CUT TO:") est un outil de montage interne entre les scènes et ne doit pas être utilisée pour terminer un scénario.',
        fixPrompt: 'Pour fermer le "couvercle" du projet : sautez deux lignes après la dernière réplique et insérez un FADE OUT. (aligné à droite) ou un FIN. (centré). Supprimez tout "COUPE SUR :" à la fin.',
        severity: 'error'
    },
    'FRM-18': {
        code: 'FRM-18',
        title: 'En-tête Orphelin (Fin de Page)',
        description: 'Un en-tête de scène apparaît seul en bas d\'une page. Les en-têtes doivent avoir au moins une ligne d\'action ou de dialogue en dessous d\'eux sur la même page.',
        fixPrompt: 'Ajoutez un Saut de Page avant l\'en-tête de scène pour le déplacer en haut de la page suivante.',
        severity: 'error'
    },
    'FRM-19': {
        code: 'FRM-19',
        title: 'Résidu de Markdown et Artéfacts',
        description: 'Le texte contient des résidus de formatage Markdown (astérisques *, underscores _, dièses # ou backticks `) ou des artéfacts de code (comme ``` ou ...) qui sont illégaux. Cela inclut les marqueurs après le FADE OUT.',
        fixPrompt: 'FOURNISSEZ LE TEXTE PROPRE SANS MARKDOWN. Enlevez ```, *, _, #. Si texte après FIN/FADE OUT, effacez-le. Texte brut uniquement.',
        severity: 'error'
    },
    'FRM-20': {
        code: 'FRM-20',
        title: 'Faux Dialogue (Action Formatée comme Dialogue)',
        description: 'Un bloc de texte centré (format Dialogue) a été détecté alors que son contenu est purement descriptif, narratif ou visuel (Action). Cela se produit souvent lorsque l\'IA confond l\'introduction d\'un personnage avec le début d\'une réplique.',
        fixPrompt: 'Convertissez IMMÉDIATEMENT ce bloc au format ACTION (Aligné à Gauche). S\'il y a un nom de personnage au-dessus (CUE), supprimez-le et intégrez le nom à la première phrase de l\'action (Ex: De MARCOS [saut] Il court. à Marcos court.).',
        severity: 'error'
    },
    'ACT-01': {
        code: 'ACT-01',
        title: 'Bloc d\'Action Trop Long',
        description: 'Les paragraphes de description doivent avoir un maximum de 4 à 5 lignes.',
        fixPrompt: 'Divisez le bloc d\'action en paragraphes plus petits de 4 lignes maximum.',
        severity: 'suggestion'
    },
    'ACT-02': {
        code: 'ACT-02',
        title: 'Direction de Caméra en Spec Script',
        description: 'Évitez les termes techniques de caméra comme CLOSE ON, ANGLE ON ou LA CAMÉRA ACCOMPAGNE dans la description. Les transitions standard comme "COUPE SUR :" sont autorisées.',
        fixPrompt: 'RÉÉCRIVEZ L\'ACTION SANS TERMES TECHNIQUES. Décrivez uniquement le visuel. N\'expliquez pas.',
        severity: 'error'
    },
    'ACT-03': {
        code: 'ACT-03',
        title: 'État Interne/Psychologie (Infilmable)',
        description: 'La description ne doit montrer que ce que la caméra enregistre. Évitez de décrire ce que le personnage "ressent", "pense", "sait" ou "réalise".',
        fixPrompt: 'TRANSFORMER EN COMPORTEMENT : NE JAMAIS juste supprimer l\'émotion. EXTERNALISEZ le sentiment abstrait par une action ou une réaction physique. Ex : Au lieu de "en état de choc", utilisez "Ses antennes tremblent et elle fait un pas en arrière". Au lieu de "Il est épuisé", utilisez "Il s\'effondre sur le sol, haletant".',
        severity: 'error'
    },
    'ACT-04': {
        code: 'ACT-04',
        title: 'Psychologie/Émotion dans l\'Action (Montre, Ne Raconte Pas)',
        description: 'Utilisation de termes purement abstraits ou internes (ex: "Elle a peur", "Détermination dans ses yeux"). Les manifestations physiques visibles (ex: "Pleure", "Crie", "Rit") sont autorisées et recommandées.',
        fixPrompt: 'TRANSFORMEZ EN COMPORTEMENT : Remplacez le sentiment abstrait par une action physique unique. S\'il est nerveux, utilisez "Ses mains tremblent".',
        severity: 'error'
    },
    'ACT-05': {
        code: 'ACT-05',
        title: 'Exposition/Explication dans l\'Action',
        description: 'Le bloc d\'action explique la biographie, l\'histoire ou les sentiments internes que la caméra ne voit pas.',
        fixPrompt: 'Réécrivez les descriptions abstraites pour être visuelles. Gardez l\'action, supprimez explications invisibles.',
        severity: 'suggestion'
    },
    'ACT-10': {
        code: 'ACT-10',
        title: 'Déchets IA / Métadonnées',
        description: 'Le scénario contient des notes de l\'IA, des salutations ou des explications techniques hors format.',
        fixPrompt: 'Supprimez uniquement les notes IA. GARDEZ TOUT LE CONTENU NARRATIF DU SCÉNARIO.',
        severity: 'error'
    },
    'ACT-11': {
        code: 'ACT-11',
        title: 'Format de Légende Non Standard',
        description: 'Utilisation de termes comme "LEGEND:" ou de formats inventés pour le texte à l\'écran.',
        fixPrompt: 'Remplacez "LEGEND:" ou similaires par le standard professionnel "TITLE ON SCREEN:" ou "SUPER:".',
        severity: 'error'
    },
    'ACT-12': {
        code: 'ACT-12',
        title: 'Langage Littéraire/Non Visuel',
        description: 'Utilisation de métaphores poétiques ou de sens non filmables dans la description de la scène.',
        fixPrompt: 'Remplacez le littéraire par le visuel/auditif. Les scénarios nécessitent une description objective du PRÉSENT.',
        severity: 'error'
    },
    'ACT-13': {
        code: 'ACT-13',
        title: 'Romanisation',
        description: 'Texte qui raconte des événements en utilisant une voix de narrateur omnisciente DANS L\'ACTION.',
        fixPrompt: 'Transformez le résumé narratif en actions présentes et brutes.',
        severity: 'error'
    },
    'ACT-14': {
        code: 'ACT-14',
        title: 'Capitalisation Son/FX',
        description: 'Les sons importants et les effets visuels cruciaux doivent être en MAJUSCULES lors de leur première apparition dans l\'action.',
        fixPrompt: 'Mettez en MAJUSCULES uniquement le nom du son ou de l\'effet visuel important mentionné (ex : UN COUP DE FEU RETENTIT).',
        severity: 'suggestion'
    },
    'ACT-15': {
        code: 'ACT-15',
        title: 'Excès d\'Adjectifs et Jugements',
        description: 'Utilisation d\'adjectifs subjectifs ou d\'évaluations déduites.',
        fixPrompt: 'OBJECTIVITÉ MAXIMALE : Remplacez les adjectifs par des mesures ou des faits physiques chaque fois que possible.',
        severity: 'suggestion'
    },
    'ACT-16': {
        code: 'ACT-16',
        title: 'Capitalisation Excessive dans l\'Action',
        description: 'Les objets courants et les lieux génériques ne doivent pas être en MAJUSCULES dans l\'action pour donner une emphase littéraire.',
        fixPrompt: 'Convertissez en minuscules. Gardez les MAJUSCULES uniquement pour les Personnages (1ère fois), les Sons Importants et les Effets Visuels.',
        severity: 'suggestion'
    },
    'ACT-17': {
        code: 'ACT-17',
        title: 'Sensations Non Visuelles dans l\'Action',
        description: 'Le scénario ne doit décrire que ce qui peut être vu ou entendu. Les sensations internes ne sont pas filmables.',
        fixPrompt: 'Remplacez les sensations comme "Forte odeur" ou "Les souvenirs reviennent" par des preuves visuelles ou auditives.',
        severity: 'error'
    },
    'ACT-18': {
        code: 'ACT-18',
        title: 'Langage Littéraire/Poétique',
        description: 'Le scénario ne doit pas ressembler à un roman ou un poème. Évitez les métaphores dramatiques sur l\'avenir ou le destin.',
        fixPrompt: 'Remplacez les phrases littéraires par des descriptions concrètes et physiques de ce qui se passe MAINTENANT.',
        severity: 'error'
    },
    'ACT-19': {
        code: 'ACT-19',
        title: 'Construction Littéraire (Prose)',
        description: 'Les termes comme "Soudainement" sont des constructions littéraires de prose. Le scénario doit décrire l\'action directement.',
        fixPrompt: 'Supprimez "Soudainement" et décrivez l\'action directement.',
        severity: 'suggestion'
    },
    'ACT-20': {
        code: 'ACT-20',
        title: 'Description Technique de l\'Image',
        description: 'Les Spec Scripts (Master Scene) ne décrivent pas la couleur de l\'image, la texture de la pellicule ou les traitements visuels de la mémoire.',
        fixPrompt: 'Supprimez les descriptions de couleur, de texture ou de traitement d\'image. Concentrez-vous exclusivement sur l\'action physique observable.',
        severity: 'suggestion'
    },
    'ACT-21': {
        code: 'ACT-21',
        title: 'Direction de Caméra Narrativa (Vu de...)',
        description: 'Évitez de décrire des angles de caméra comme "Vu du dessus", "Aérienne" ou "Point de vue". Concentrez-vous sur l\'action, pas sur l\'objectif.',
        fixPrompt: 'Réécrivez en vous concentrant sur l\'action. De : "Vu du dessus : Les nageurs..." À : "Les nageurs forment une ligne dans l\'eau...".',
        severity: 'suggestion'
    },
    'ACT-22': {
        code: 'ACT-22',
        title: 'Méta-Description ou Résumé de Scène',
        description: 'Le texte décrit que "nous voyons une scène" ou "une séquence de..." au lieu de décrire l\'action directe. Le scénario doit montrer l\'événement, pas résumer son existence.',
        fixPrompt: 'TRANSFORMEZ EN ACTION DIRECTE : Au lieu de "On voit des scènes d\'enfants jouant", décrivez "Des enfants sautent à la corde et courent". Supprimez "Scènes de...", "Séquence de..." ou "On voit".',
        severity: 'error'
    },
    'DLG-01': {
        code: 'DLG-01',
        title: 'Dialogue Explicatif (On-the-nose)',
        description: 'Le personnage dit exactement ce qu\'il ressent ou explique l\'intrigue de manière artificielle.',
        fixPrompt: 'Affinez le dialogue pour utiliser le sous-texte.',
        severity: 'suggestion'
    },
    'DLG-02': {
        code: 'DLG-02',
        title: 'V.O. / Narration Formatée comme Action',
        description: 'Les indications de narration ou les lignes de Voix Off ne doivent pas être dans des blocs de description.',
        fixPrompt: 'Formateur correctement comme Dialogue : NOM (V.O.) suivi du texte du discours.',
        severity: 'error'
    },
    'DLG-03': {
        code: 'DLG-03',
        title: 'Action Dans le Dialogue (Règle de Rupture)',
        description: 'Il est STRICTEMENT INTERDIT de décrire des actions physiques ou une narration de scène à l\'intérieur d\'un bloc de dialogue. Cela inclut les cas où le bloc entier est une description narrative à la troisième personne (action) positionnée comme parole. ATTENTION : Si le personnage mentionne simplement une action ou un objet (ex : "Je vais sortir maintenant"), c\'est un DIALOGUE légitime et cela ne doit PAS être marqué comme une erreur.',
        fixPrompt: 'APPLIQUER LA LOI DE RUPTURE : Extrayez l\'action dans un nouveau paragraphe d\'Action. Si le bloc décrit un son (ex : grogner, rire, crier), vous DEVEZ "guérir" la parole en insérant une onomatopée appropriée (ex : "Grrrrrrr", "Hahaha", "Aaaaah !") cohérente avec la rubrique, en maintenant le bloc de parole actif, puis déplacez l\'action vers le bas.',
        severity: 'error'
    },
    'DLG-04': {
        code: 'DLG-04',
        title: 'Parenthèse Interprétative',
        description: 'Les parenthèses doivent indiquer uniquement l\'INTENTION ou le TON DE VOIX (ex: murmure, sarcastique). Maximum 3 mots. INTERDIT de décrire des actions physiques. INTERDIT: sauts de ligne ou césures. OBLIGATOIRE: Texte de la parenthèse en lettres minuscules (ex: "murmure"). PAS D\'ESPACES: N\'utilisez jamais d\'espaces avant ou à l\'intérieur (ex: " (texte)" ou "( texte )"). LA PARENTHÈSE DOIT ÊTRE SEULE SUR SA PROPRE LIGNE.',
        fixPrompt: 'Retirez l\'action physique de la parenthèse. Si c\'est une action visible ou si elle fait plus de 3 mots, déplacez-la vers une ligne d\'Action. Convertissez le texte en minuscules. Gardez entre parenthèses uniquement le ton de la voix. SUPPRIMEZ LES ESPACES: Assurez-vous que la parenthèse/crochet est collé au texte et qu\'il n\'y a pas d\'espaces en début de ligne.',
        severity: 'error'
    },
    'DLG-06': {
        code: 'DLG-06',
        title: 'Dialogue sur la Même Ligne que la Parenthèse',
        description: 'Le dialogue ne doit jamais commencer sur la même ligne que la parenthèse. La parenthèse doit occuper sa propre ligne.',
        fixPrompt: 'Ajoutez un saut de ligne après la parenthèse fermante pour déplacer le dialogue à la ligne suivante.',
        severity: 'error'
    },
    'DLG-07': {
        code: 'DLG-07',
        title: 'Césure dans le Corps du Dialogue',
        description: 'Les mots ne doivent pas comporter de traits d\'union de coupure ni être brisés entre deux lignes dans le bloc de dialogue. Le mot doit être conservé en entier sur la ligne actuelle ou déplacé intégralement sur la suivante.',
        fixPrompt: 'Supprimez les traits d\'union de césure et assurez-vous que le terme est entier sur une seule ligne.',
        severity: 'error'
    },
    'DLG-05': {
        code: 'DLG-05',
        title: 'Capitalisation Irrégulière dans le Dialogue',
        description: 'Utilisation de lettres majuscules au milieu des phrases sans justification.',
        fixPrompt: 'Corrigez la capitalisation pour suivre les règles grammaticales standard.',
        severity: 'suggestion'
    },
    'DLG-08': {
        code: 'DLG-08',
        title: 'Écho de Transcription (Dialogue Dupliqué)',
        description: 'Le texte exact du dialogue a été répété ci-dessous comme une ligne d\'action, ou l\'ensemble du dialogue est indûment en MAJUSCULES.',
        fixPrompt: 'Supprimez la ligne d\'action en double et convertissez le texte du dialogue en format de phrase normale (seule la première lettre en majuscule).',
        severity: 'error'
    },
    'CHR-01': {
        code: 'CHR-01',
        title: 'Introduction de Personnage Non Standard',
        description: 'Utilisation incorrecte de concepts narratifs au lieu d\'une description visuelle dans l\'introduction.',
        fixPrompt: 'Ajustez en : NOM (TYPE/ÂGE), description visuelle.',
        severity: 'error'
    },
    'CHR-02': {
        code: 'CHR-02',
        title: 'Nom de Personnage en Minuscule',
        description: 'Le nom du personnage au-dessus du dialogue doit être en MAJUSCULES.',
        fixPrompt: 'Mettez le nom du personnage en MAJUSCULES.',
        severity: 'error'
    },
    'CHR-03': {
        code: 'CHR-03',
        title: 'Première Apparition du Personnage',
        description: 'La première fois qu\'un personnage est présenté dans l\'Action, son nom doit être en MAJUSCULES. ATTENTION : Les personnages ne sont PAS des SFX.',
        fixPrompt: 'Capitalisez le nom du personnage lors de cette première apparition.',
        severity: 'error'
    },
    'CHR-04': {
        code: 'CHR-04',
        title: 'Personnage Fantôme (Voix sans Corps)',
        description: 'Un personnage a une ligne (DIALOGUE) mais n\'a pas été introduit ou mentionné dans l\'action immédiatement avant.',
        fixPrompt: 'Insérez une ligne d\'action avant le dialogue décrivant le personnage ou son entrée (ex : "UN PLONGEUR ajuste ses lunettes.").',
        severity: 'error'
    },
    'LOG-01': {
        code: 'LOG-01',
        title: 'Erreur de Permanence d\'Objet',
        description: 'Un objet apparaît ou disparaît sans explication logique ou mention préalable.',
        fixPrompt: 'Ajoutez une action précédente où le personnage ramasse/porte l\'objet ou supprimez l\'utilisation de l\'objet s\'il ne devrait pas être là.',
        severity: 'error'
    },
    'LOG-02': {
        code: 'LOG-02',
        title: 'Action Physiquement Impossible',
        description: 'Le personnage effectue une action qui contredit la physique du décor ou sa position précédente.',
        fixPrompt: 'Ajustez l\'action pour qu\'elle soit physiquement possible dans le contexte spatial établi.',
        severity: 'error'
    },
    'LOG-03': {
        code: 'LOG-03',
        title: 'Décor Impossible (INT./EXT.)',
        description: 'L\'en-tête définit "INT." pour un lieu naturellement ouvert (ex: MER, CIEL) sans être contenu. La mer est toujours EXT.',
        fixPrompt: 'Passez à EXT. si c\'est en extérieur. Pour sous l\'eau, utilisez "EXT. MER - SOUS L\'EAU". Utilisez INT. seulement si DANS une structure.',
        severity: 'error'
    },
    'STR-01': {
        code: 'STR-01',
        title: 'Absence de Charge de Valeur',
        description: 'La scène se termine dans le même état émotionnel ou situationnel qu\'elle a commencé. Une scène doit faire tourner la valeur (ex : de Positif à Négatif).',
        fixPrompt: 'Introduisez un conflit ou une révélation qui change la charge de la scène.',
        severity: 'warning'
    },
    'STR-02': {
        code: 'STR-02',
        title: 'Manque d\'Objectif du Personnage',
        description: 'Le protagoniste de la scène ne semble pas avoir d\'objectif clair ou de besoin dramatique immédiat.',
        fixPrompt: 'Établissez ce que le personnage veut accomplir dans cette scène spécifique.',
        severity: 'warning'
    },
    'STR-03': {
        code: 'STR-03',
        title: 'Absence de Conflit/Antagonisme',
        description: 'La scène progresse sans résistance. Les personnages sont d\'accord trop facilement ou il n\'y a pas de forces opposées.',
        fixPrompt: 'Introduisez des obstacles, qu\'ils soient internes (doute), interpersonnels (un autre personnage) ou environnementaux.',
        severity: 'warning'
    },
    'STR-04': {
        code: 'STR-04',
        title: 'Rythme Statique',
        description: 'La scène ne consiste qu\'en dialogues ou en actions répétitives, sans progression narrative.',
        fixPrompt: 'Variez le rythme. Intercalez des dialogues significatifs avec des moments d\'action qui font avancer l\'histoire.',
        severity: 'warning'
    },
    'HDR-01': {
        code: 'HDR-01',
        title: 'Incohérence de Nom de Scène',
        description: 'Le même lieu doit être nommé de manière identique dans tous les en-têtes pour éviter des erreurs lors du dépouillement de production.',
        fixPrompt: 'Standardisez le nom de cette scène en utilisant le terme déjà établi précédemment dans le scénario.',
        severity: 'warning'
    },
    'ACT-23': {
        code: 'ACT-23',
        title: 'Mise en Valeur Technique (SFX/Props)',
        description: 'Les effets sonores (SFX) et les accessoires (Props) importants doivent être en MAJUSCULES lors de leur première apparition.',
        fixPrompt: 'Mettez le terme technique ou l\'objet fondamental en MAJUSCULES.',
        severity: 'suggestion'
    },
    'STY-01': {
        code: 'STY-01',
        title: 'Nettoyage de Spec Script',
        description: 'Supprimez les numéros de scène latéraux et les transitions redondantes (ex: COUPE SUR :) qui n\'ont pas de fonction dramatique spécifique.',
        fixPrompt: 'Supprimez les numéros de scène et nettoyez les transitions inutiles pour le standard Master Scene.',
        severity: 'error'
    },
};

export const cleanCode = (code: string) => {
    let cleaned = code.replace(/[\[\]\s]/g, '').toUpperCase();
    if (/^[A-Z]{3}\d{1,2}$/.test(cleaned)) {
        cleaned = cleaned.substring(0, 3) + '-' + cleaned.substring(3);
    }
    return cleaned;
};

export const LOCALIZED_ERRORS: Record<string, Record<string, AuditErrorDefinition>> = {
    pt: AUDIT_ERRORS,
    en: AUDIT_ERRORS_EN,
    es: AUDIT_ERRORS_ES,
    fr: AUDIT_ERRORS_FR
};

/**
 * MECHANICAL_RULES_EXCLUSION
 * List of rules handled surgically by the Mechanical Linter (Regex/Parser).
 * These are excluded from AI cognition to prevent "Goal Overload" and conflicting corrections.
 */
export const MECHANICAL_RULES_EXCLUSION = [
    'FRM-01', 'FRM-02', 'FRM-03', 'FRM-04', 'FRM-05', 'FRM-07', 'FRM-08', 'FRM-09',
    'FRM-10', 'FRM-11', 'FRM-12', 'FRM-13', 'FRM-14', 'FRM-16', 'FRM-17', 'FRM-18', 'FRM-19',
    'ACT-01', 'ACT-10', 'ACT-11', 'ACT-14', 'ACT-15', 'ACT-16',
    'CHR-01', 'CHR-02', 'CHR-03',
    'DLG-02', 'DLG-05', 'DLG-06',
    'LOG-03'
];

export const getErrorMessage = (code: string, language: string = 'pt') => {
    const cleaned = cleanCode(code);
    const lang = LOCALIZED_ERRORS[language] ? language : 'pt';
    const error = LOCALIZED_ERRORS[lang][cleaned] || AUDIT_ERRORS[cleaned];
    return error?.description || 'Erro não identificado.';
};

export const getErrorFix = (code: string, language: string = 'pt') => {
    const cleaned = cleanCode(code);
    const lang = LOCALIZED_ERRORS[language] ? language : 'pt';
    const error = LOCALIZED_ERRORS[lang][cleaned] || AUDIT_ERRORS[cleaned];
    return error?.fixPrompt || 'Ajuste conforme o padrão Master Scene.';
};

export const getErrorTitle = (code: string, language: string = 'pt') => {
    const cleaned = cleanCode(code);
    const lang = LOCALIZED_ERRORS[language] ? language : 'pt';
    const error = LOCALIZED_ERRORS[lang][cleaned] || AUDIT_ERRORS[cleaned];
    return error?.title || 'Problema de Formatação';
};
